/**
 * Simplified Estimate Table - Based on Labor Agent Data
 * No longer uses project key - reads from estimate_labor_steps table
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../lib/supabase';
import { Calculator, RefreshCw, Download, Clock, Layers, DollarSign, Receipt, Trash2, Plus, FileDown } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../contexts/AuthContext';
import '../styles/SimplifiedEstimate.css';

interface LaborStep {
  step_number: number;
  step_description: string;
  quantity: number;
  quantity_unit: string;
  matched_component: string;
  measurement: string;
  normal_labor_units: number;
  difficult_labor_units: number;
  very_difficult_labor_units: number;
  unit: string;
  section: string;
  page_number: number;
  // Material scraping fields
  material_cost?: number;
  material_product_title?: string;
  material_product_url?: string;
}

interface SimplifiedEstimateTableProps {
  estimationId: string;
  projectName?: string;
}

export const SimplifiedEstimateTable: React.FC<SimplifiedEstimateTableProps> = ({
  estimationId,
  projectName
}) => {
  const { username } = useAuth();
  const [laborSteps, setLaborSteps] = useState<LaborStep[]>([]);
  
  // String inputs for controlled editing (no auto-refresh)
  const [laborRateInput, setLaborRateInput] = useState<string>('200.00');
  const [overheadInput, setOverheadInput] = useState<string>('5.00');
  const [profitInput, setProfitInput] = useState<string>('15.00');
  const [materialTaxRateInput, setMaterialTaxRateInput] = useState<string>('3.94');
  
  // Shorthand string inputs
  const [shorthandMaterialCostInput, setShorthandMaterialCostInput] = useState<string>('0.00');
  const [shorthandLaborHoursInput, setShorthandLaborHoursInput] = useState<string>('0.00');
  const [shorthandPMLaborRateInput, setShorthandPMLaborRateInput] = useState<string>('0.00');
  const [shorthandPMHoursInput, setShorthandPMHoursInput] = useState<string>('0.00');
  
  // Number values for calculations
  const [laborRate, setLaborRate] = useState<number>(200);
  const [laborCondition, setLaborCondition] = useState<'normal' | 'difficult' | 'very_difficult'>('normal');
  const [overhead, setOverhead] = useState<number>(5);
  const [profit, setProfit] = useState<number>(15);
  const [materialTaxRate, setMaterialTaxRate] = useState<number>(3.94);
  
  // NECA condition selection (normal = NECA 1, difficult = NECA 2, very_difficult = NECA 3)
  const [necaCondition, setNecaCondition] = useState<'normal' | 'difficult' | 'very_difficult'>('normal');
  const [showNecaDropdown, setShowNecaDropdown] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [editedSteps, setEditedSteps] = useState<Map<string, { quantity?: number; materialCost?: number; laborHours?: number }>>(new Map());
  const [rowSettings, setRowSettings] = useState<Map<string, { laborRate: number; overhead: number; profit: number }>>(new Map());
  const [rowNecaConditions, setRowNecaConditions] = useState<Map<string, 'normal' | 'difficult' | 'very_difficult'>>(new Map());
  const [savedCustomHours, setSavedCustomHours] = useState<Map<string, number>>(new Map()); // Preserve custom hours when switching modes
  const [editingMaterialCell, setEditingMaterialCell] = useState<string | null>(null);
  const [editingCell, setEditingCell] = useState<{ key: string; field: string } | null>(null);
  const [cellInputValue, setCellInputValue] = useState<string>('');
  
  // Shorthand number values for calculations
  const [shorthandMaterialCost, setShorthandMaterialCost] = useState<number>(0);
  const [shorthandLaborHours, setShorthandLaborHours] = useState<number>(0);
  const [shorthandPMLaborRate, setShorthandPMLaborRate] = useState<number>(0);
  const [shorthandPMHours, setShorthandPMHours] = useState<number>(0);
  
  // Add New Item Modal
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [addItemMode, setAddItemMode] = useState<'electrical' | 'pm' | 'misc'>('electrical');
  const [pmItems, setPmItems] = useState<any[]>([]);
  const [miscItems, setMiscItems] = useState<any[]>([]);
  
  // PDF Export Dropdown
  const [showPdfExportDropdown, setShowPdfExportDropdown] = useState(false);
  
  // Change Order Modal
  const [showChangeOrderModal, setShowChangeOrderModal] = useState(false);
  const [changeOrderCCN, setChangeOrderCCN] = useState('');
  const [changeOrderClientAddress, setChangeOrderClientAddress] = useState('');
  const [changeOrderWorkDescription, setChangeOrderWorkDescription] = useState('');
  
  // Incidental Labor Items
  const [incidentalLaborItems, setIncidentalLaborItems] = useState<any[]>([]);
  const [showAddIncidentalLaborModal, setShowAddIncidentalLaborModal] = useState(false);
  const [newIncidentalDescription, setNewIncidentalDescription] = useState('');
  const [newIncidentalHours, setNewIncidentalHours] = useState('');
  const [newIncidentalRate, setNewIncidentalRate] = useState('');
  
  // Indirect Labor Items
  const [indirectLaborItems, setIndirectLaborItems] = useState<any[]>([]);
  const [showAddIndirectLaborModal, setShowAddIndirectLaborModal] = useState(false);
  const [newIndirectDescription, setNewIndirectDescription] = useState('');
  const [newIndirectHours, setNewIndirectHours] = useState('');
  const [newIndirectRate, setNewIndirectRate] = useState('');
  
  // Subcontractor Items
  const [subcontractorItems, setSubcontractorItems] = useState<any[]>([]);
  const [showAddSubcontractorModal, setShowAddSubcontractorModal] = useState(false);
  const [newSubcontractorName, setNewSubcontractorName] = useState('');
  const [newSubcontractorDescription, setNewSubcontractorDescription] = useState('');
  const [newSubcontractorPrice, setNewSubcontractorPrice] = useState('');
  
  // Equipment Items
  const [equipmentItems, setEquipmentItems] = useState<any[]>([]);
  const [showAddEquipmentModal, setShowAddEquipmentModal] = useState(false);
  const [newEquipmentName, setNewEquipmentName] = useState('');
  const [newEquipmentDescription, setNewEquipmentDescription] = useState('');
  const [newEquipmentPrice, setNewEquipmentPrice] = useState('');
  
  // Quoted Materials Items
  const [quotedMaterialsItems, setQuotedMaterialsItems] = useState<any[]>([]);
  const [showAddQuotedMaterialsModal, setShowAddQuotedMaterialsModal] = useState(false);
  const [newQuotedMaterialName, setNewQuotedMaterialName] = useState('');
  const [newQuotedMaterialDescription, setNewQuotedMaterialDescription] = useState('');
  const [newQuotedMaterialQty, setNewQuotedMaterialQty] = useState('');
  const [newQuotedMaterialUnit, setNewQuotedMaterialUnit] = useState('EA');
  const [newQuotedMaterialPrice, setNewQuotedMaterialPrice] = useState('');
  
  // Ground Management Items (Foreman, General Foreman, Superintendent, etc.)
  const [groundManagementItems, setGroundManagementItems] = useState<any[]>([]);
  const [showAddGroundManagementModal, setShowAddGroundManagementModal] = useState(false);
  const [newGroundManagementRole, setNewGroundManagementRole] = useState('Foreman');
  const [newGroundManagementHours, setNewGroundManagementHours] = useState('');
  const [newGroundManagementRate, setNewGroundManagementRate] = useState('');
  
  // New item form fields
  const [newItemName, setNewItemName] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [newItemQuantity, setNewItemQuantity] = useState('1.00');
  const [newItemUnit, setNewItemUnit] = useState('EA');
  const [newItemLaborHours, setNewItemLaborHours] = useState('0.00');
  const [newItemMaterialPrice, setNewItemMaterialPrice] = useState('0.00');
  const [newPMHours, setNewPMHours] = useState('0.00');
  const [newPMRate, setNewPMRate] = useState('0.00');
  const [newMiscPrice, setNewMiscPrice] = useState('0.00');
  
  // Format number with commas
  const formatCurrency = (value: number): string => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };
  
  // Format number with commas (for non-currency)
  const formatNumber = (value: number): string => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };
  
  // Parse input by removing commas
  const parseInput = (value: string): number => {
    return parseFloat(value.replace(/,/g, '')) || 0;
  };
  
  // Get row-specific settings or defaults
  const getRowSettings = (stepKey: string) => {
    return rowSettings.get(stepKey) || { laborRate, overhead, profit };
  };
  
  // Update row setting and save to Supabase
  const updateRowSetting = async (stepKey: string, field: 'laborRate' | 'overhead' | 'profit', value: number) => {
    const current = rowSettings.get(stepKey) || { laborRate, overhead, profit };
    const updated = { ...current, [field]: value };
    setRowSettings(new Map(rowSettings.set(stepKey, updated)));
    
    // Save to Supabase
    try {
      const [matched_component, measurement] = stepKey.split('-');
      const { error } = await supabase
        .from('estimate_labor_steps')
        .update({
          row_labor_rate: updated.laborRate,
          row_overhead: updated.overhead,
          row_profit: updated.profit
        })
        .eq('estimation_id', estimationId)
        .eq('matched_component', matched_component)
        .eq('measurement', measurement);
      
      if (error) {
        console.error('Error saving row settings:', error);
      } else {
        console.log(`✅ Saved ${field} for ${stepKey}`);
      }
    } catch (error) {
      console.error('Exception saving row settings:', error);
    }
  };
  
  // Delete row from estimate table
  const deleteRow = async (step: LaborStep) => {
    try {
      const { error } = await supabase
        .from('estimate_labor_steps')
        .delete()
        .eq('estimation_id', estimationId)
        .eq('matched_component', step.matched_component)
        .eq('measurement', step.measurement);
      
      if (error) {
        console.error('Error deleting row:', error);
        alert('Failed to delete row. Please try again.');
      } else {
        console.log(`✅ Deleted ${step.matched_component}`);
        // Immediately reload the table
        await loadLaborSteps();
      }
    } catch (error) {
      console.error('Exception deleting row:', error);
      alert('Failed to delete row. Please try again.');
    }
  };

  // Add new electrical component
  const addElectricalComponent = async () => {
    if (!newItemName.trim()) {
      alert('Please enter a component name');
      return;
    }
    
    try {
      const quantity = parseInput(newItemQuantity) || 1;
      const laborHours = parseInput(newItemLaborHours) || 0;
      const materialCost = parseInput(newItemMaterialPrice) || 0;
      
      const { error } = await supabase
        .from('estimate_labor_steps')
        .insert({
          estimation_id: estimationId,
          step_number: laborSteps.length + 1,
          step_description: newItemDescription || newItemName,
          quantity: quantity,
          quantity_unit: newItemUnit,
          matched_component: newItemName,
          measurement: newItemDescription || 'Custom',
          normal_labor_units: laborHours / quantity,
          unit: 'E',
          material_cost: materialCost,
        });
      
      if (error) throw error;
      
      console.log('✅ Added electrical component');
      await loadLaborSteps();
      resetModal();
    } catch (error) {
      console.error('Error adding electrical component:', error);
      alert('Failed to add component');
    }
  };
  
  // Add new PM item
  const addPMItem = async () => {
    try {
      const hours = parseInput(newPMHours) || 0;
      const rate = parseInput(newPMRate) || 0;
      
      const { error } = await supabase
        .from('estimate_project_management')
        .insert({
          estimation_id: estimationId,
          item_name: 'Project Management',
          labor_hours: hours,
          pm_hourly_rate: rate,
        });
      
      if (error) throw error;
      
      console.log('✅ Added PM item');
      await loadPMItems();
      resetModal();
    } catch (error) {
      console.error('Error adding PM item:', error);
      alert('Failed to add PM item');
    }
  };
  
  // Add new miscellaneous item
  const addMiscItem = async () => {
    if (!newItemName.trim()) {
      alert('Please enter an item name');
      return;
    }
    
    try {
      const price = parseInput(newMiscPrice) || 0;
      
      const { error } = await supabase
        .from('estimate_miscellaneous')
        .insert({
          estimation_id: estimationId,
          item_name: newItemName,
          description: newItemDescription,
          price: price,
        });
      
      if (error) throw error;
      
      console.log('✅ Added misc item');
      await loadMiscItems();
      resetModal();
    } catch (error) {
      console.error('Error adding misc item:', error);
      alert('Failed to add misc item');
    }
  };
  
  // Reset modal
  const resetModal = () => {
    setShowAddItemModal(false);
    setNewItemName('');
    setNewItemDescription('');
    setNewItemQuantity('1.00');
    setNewItemUnit('EA');
    setNewItemLaborHours('0.00');
    setNewItemMaterialPrice('0.00');
    setNewPMHours('0.00');
    setNewPMRate('0.00');
    setNewMiscPrice('0.00');
  };
  
  // Open modal with default PM hours
  const openAddItemModal = () => {
    setShowAddItemModal(true);
    // Set default PM hours to 1/5 of total labor hours
    const defaultPMHours = totals.laborHours / 5;
    setNewPMHours(formatNumber(defaultPMHours));
    setNewPMRate(formatNumber(laborRate));
  };

  // Load labor steps, PM items, misc items, and all manual entry items from database
  useEffect(() => {
    loadLaborSteps();
    loadPMItems();
    loadMiscItems();
    loadIncidentalLaborItems();
    loadIndirectLaborItems();
    loadSubcontractorItems();
    loadEquipmentItems();
    loadQuotedMaterialsItems();
    loadGroundManagementItems();
  }, [estimationId]);
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
      if (showNecaDropdown) {
        if (!target.closest('th[style*="cursor: pointer"]')) {
          setShowNecaDropdown(false);
        }
      }
      if (showPdfExportDropdown) {
        if (!target.closest('[data-pdf-dropdown]')) {
          setShowPdfExportDropdown(false);
        }
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showNecaDropdown, showPdfExportDropdown]);
  
  // Subscribe to realtime updates for this estimation
  useEffect(() => {
    const estimationChannel = supabase
      .channel(`estimation-${estimationId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'estimations',
          filter: `id=eq.${estimationId}`
        },
        (payload) => {
          console.log('🔄 Estimation updated, reloading data...', payload);
          // Only reload global settings and shorthand values, not the entire table
          const newData = payload.new as any;
          if (newData.labor_rate_per_hour !== undefined) {
            setLaborRate(newData.labor_rate_per_hour);
            setLaborRateInput(formatNumber(newData.labor_rate_per_hour));
          }
          if (newData.overhead_percentage !== undefined) {
            setOverhead(newData.overhead_percentage);
            setOverheadInput(formatNumber(newData.overhead_percentage));
          }
          if (newData.profit_percentage !== undefined) {
            setProfit(newData.profit_percentage);
            setProfitInput(formatNumber(newData.profit_percentage));
          }
          if (newData.material_tax_rate !== undefined) {
            setMaterialTaxRate(newData.material_tax_rate);
            setMaterialTaxRateInput(formatNumber(newData.material_tax_rate));
          }
          if (newData.shorthand_material_cost !== undefined) {
            setShorthandMaterialCost(newData.shorthand_material_cost);
            setShorthandMaterialCostInput(formatNumber(newData.shorthand_material_cost));
          }
          if (newData.shorthand_labor_hours !== undefined) {
            setShorthandLaborHours(newData.shorthand_labor_hours);
            setShorthandLaborHoursInput(formatNumber(newData.shorthand_labor_hours));
          }
          if (newData.shorthand_pm_labor_rate !== undefined) {
            setShorthandPMLaborRate(newData.shorthand_pm_labor_rate);
            setShorthandPMLaborRateInput(formatNumber(newData.shorthand_pm_labor_rate));
          }
          if (newData.shorthand_pm_hours !== undefined) {
            setShorthandPMHours(newData.shorthand_pm_hours);
            setShorthandPMHoursInput(formatNumber(newData.shorthand_pm_hours));
          }
          if (newData.neca_condition !== undefined) {
            setNecaCondition(newData.neca_condition);
          }
        }
      )
      .subscribe();
    
    // Subscribe to labor steps changes (for when agent adds new labor calculations)
    const laborStepsChannel = supabase
      .channel(`labor-steps-${estimationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'estimate_labor_steps',
          filter: `estimation_id=eq.${estimationId}`
        },
        (payload) => {
          console.log('🔄 Labor steps updated, reloading table...', payload);
          loadLaborSteps();
        }
      )
      .subscribe();
    
    // Subscribe to PM items changes
    const pmChannel = supabase
      .channel(`pm-items-${estimationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'estimate_project_management',
          filter: `estimation_id=eq.${estimationId}`
        },
        (payload) => {
          console.log('🔄 PM items updated, reloading...', payload);
          loadPMItems();
        }
      )
      .subscribe();
    
    // Subscribe to misc items changes
    const miscChannel = supabase
      .channel(`misc-items-${estimationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'estimate_miscellaneous',
          filter: `estimation_id=eq.${estimationId}`
        },
        (payload) => {
          console.log('🔄 Misc items updated, reloading...', payload);
          loadMiscItems();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(estimationChannel);
      supabase.removeChannel(laborStepsChannel);
      supabase.removeChannel(pmChannel);
      supabase.removeChannel(miscChannel);
    };
  }, [estimationId]);
  
  // Update all row labor rates when global labor rate changes
  useEffect(() => {
    if (laborSteps.length === 0) return;
    
    const updatedSettings = new Map<string, { laborRate: number; overhead: number; profit: number }>();
    laborSteps.forEach(step => {
      const key = `${step.matched_component}-${step.measurement}-${step.step_description || ''}`;
      const current = rowSettings.get(key) || { laborRate, overhead, profit };
      updatedSettings.set(key, { ...current, laborRate });
    });
    setRowSettings(updatedSettings);
    
    // Save to Supabase - update all rows
    laborSteps.forEach(async (step) => {
      try {
        await supabase
          .from('estimate_labor_steps')
          .update({ row_labor_rate: laborRate })
          .eq('estimation_id', estimationId)
          .eq('matched_component', step.matched_component)
          .eq('measurement', step.measurement);
      } catch (error) {
        console.error('Error updating labor rate:', error);
      }
    });
  }, [laborRate]);
  
  // Update all row overhead when global overhead changes
  useEffect(() => {
    if (laborSteps.length === 0) return;
    
    const updatedSettings = new Map<string, { laborRate: number; overhead: number; profit: number }>();
    laborSteps.forEach(step => {
      const key = `${step.matched_component}-${step.measurement}-${step.step_description || ''}`;
      const current = rowSettings.get(key) || { laborRate, overhead, profit };
      updatedSettings.set(key, { ...current, overhead });
    });
    setRowSettings(updatedSettings);
    
    // Save to Supabase - update all rows
    laborSteps.forEach(async (step) => {
      try {
        await supabase
          .from('estimate_labor_steps')
          .update({ row_overhead: overhead })
          .eq('estimation_id', estimationId)
          .eq('matched_component', step.matched_component)
          .eq('measurement', step.measurement);
      } catch (error) {
        console.error('Error updating overhead:', error);
      }
    });
  }, [overhead]);
  
  // Update all row profit when global profit changes
  useEffect(() => {
    if (laborSteps.length === 0) return;
    
    const updatedSettings = new Map<string, { laborRate: number; overhead: number; profit: number }>();
    laborSteps.forEach(step => {
      const key = `${step.matched_component}-${step.measurement}-${step.step_description || ''}`;
      const current = rowSettings.get(key) || { laborRate, overhead, profit };
      updatedSettings.set(key, { ...current, profit });
    });
    setRowSettings(updatedSettings);
    
    // Save to Supabase - update all rows
    laborSteps.forEach(async (step) => {
      try {
        await supabase
          .from('estimate_labor_steps')
          .update({ row_profit: profit })
          .eq('estimation_id', estimationId)
          .eq('matched_component', step.matched_component)
          .eq('measurement', step.measurement);
      } catch (error) {
        console.error('Error updating profit:', error);
      }
    });
  }, [profit]);
  
  // Load PM items
  const loadPMItems = async () => {
    try {
      const { data, error } = await supabase
        .from('estimate_project_management')
        .select('*')
        .eq('estimation_id', estimationId)
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Error loading PM items:', error);
      } else {
        setPmItems(data || []);
      }
      } catch (error) {
      console.error('Exception loading PM items:', error);
    }
  };
  
  // Load miscellaneous items
  const loadMiscItems = async () => {
    try {
      const { data, error } = await supabase
        .from('estimate_miscellaneous')
        .select('*')
        .eq('estimation_id', estimationId)
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Error loading misc items:', error);
      } else {
        setMiscItems(data || []);
      }
      } catch (error) {
      console.error('Exception loading misc items:', error);
    }
  };
  
  // Load incidental labor items
  const loadIncidentalLaborItems = async () => {
    try {
      const { data, error } = await supabase
        .from('estimate_incidental_labor')
        .select('*')
        .eq('estimation_id', estimationId)
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Error loading incidental labor items:', error);
      } else {
        setIncidentalLaborItems(data || []);
      }
    } catch (error) {
      console.error('Exception loading incidental labor items:', error);
    }
  };
  
  // Add incidental labor item
  const addIncidentalLaborItem = async () => {
    if (!newIncidentalDescription.trim()) {
      alert('Please enter a description');
      return;
    }
    
    try {
      const hours = parseInput(newIncidentalHours) || 0;
      const rate = parseInput(newIncidentalRate) || laborRate;
      
      const { error } = await supabase
        .from('estimate_incidental_labor')
        .insert({
          estimation_id: estimationId,
          description: newIncidentalDescription,
          hours: hours,
          hourly_rate: rate
        });
      
      if (error) throw error;
      
      console.log('✅ Added incidental labor item');
      await loadIncidentalLaborItems();
      setShowAddIncidentalLaborModal(false);
      setNewIncidentalDescription('');
      setNewIncidentalHours('');
      setNewIncidentalRate('');
    } catch (error) {
      console.error('Error adding incidental labor item:', error);
      alert('Failed to add incidental labor item');
    }
  };
  
  // Delete incidental labor item
  const deleteIncidentalLaborItem = async (itemId: string) => {
    if (!window.confirm('Delete this incidental labor item?')) return;
    
    try {
      const { error } = await supabase
        .from('estimate_incidental_labor')
        .delete()
        .eq('id', itemId);
      
      if (error) throw error;
      
      await loadIncidentalLaborItems();
    } catch (error) {
      console.error('Error deleting incidental labor item:', error);
      alert('Failed to delete item');
    }
  };
  
  // Load indirect labor items
  const loadIndirectLaborItems = async () => {
    try {
      const { data, error } = await supabase
        .from('estimate_indirect_labor')
        .select('*')
        .eq('estimation_id', estimationId)
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Error loading indirect labor items:', error);
      } else {
        setIndirectLaborItems(data || []);
      }
    } catch (error) {
      console.error('Exception loading indirect labor items:', error);
    }
  };
  
  // Add indirect labor item
  const addIndirectLaborItem = async () => {
    if (!newIndirectDescription.trim()) {
      alert('Please enter a description');
      return;
    }
    
    try {
      const hours = parseInput(newIndirectHours) || 0;
      const rate = parseInput(newIndirectRate) || laborRate;
      
      const { error } = await supabase
        .from('estimate_indirect_labor')
        .insert({
          estimation_id: estimationId,
          description: newIndirectDescription,
          hours: hours,
          hourly_rate: rate
        });
      
      if (error) throw error;
      
      console.log('✅ Added indirect labor item');
      await loadIndirectLaborItems();
      setShowAddIndirectLaborModal(false);
      setNewIndirectDescription('');
      setNewIndirectHours('');
      setNewIndirectRate('');
    } catch (error) {
      console.error('Error adding indirect labor item:', error);
      alert('Failed to add indirect labor item');
    }
  };
  
  // Delete indirect labor item
  const deleteIndirectLaborItem = async (itemId: string) => {
    if (!window.confirm('Delete this indirect labor item?')) return;
    
    try {
      const { error } = await supabase
        .from('estimate_indirect_labor')
        .delete()
        .eq('id', itemId);
      
      if (error) throw error;
      
      await loadIndirectLaborItems();
    } catch (error) {
      console.error('Error deleting indirect labor item:', error);
      alert('Failed to delete item');
    }
  };
  
  // Load subcontractor items
  const loadSubcontractorItems = async () => {
    try {
      const { data, error } = await supabase
        .from('estimate_subcontractors')
        .select('*')
        .eq('estimation_id', estimationId)
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Error loading subcontractor items:', error);
      } else {
        setSubcontractorItems(data || []);
      }
    } catch (error) {
      console.error('Exception loading subcontractor items:', error);
    }
  };
  
  // Add subcontractor item
  const addSubcontractorItem = async () => {
    if (!newSubcontractorName.trim()) {
      alert('Please enter a subcontractor name');
      return;
    }
    
    try {
      const price = parseInput(newSubcontractorPrice) || 0;
      
      const { error } = await supabase
        .from('estimate_subcontractors')
        .insert({
          estimation_id: estimationId,
          name: newSubcontractorName,
          description: newSubcontractorDescription,
          price: price
        });
      
      if (error) throw error;
      
      console.log('✅ Added subcontractor item');
      await loadSubcontractorItems();
      setShowAddSubcontractorModal(false);
      setNewSubcontractorName('');
      setNewSubcontractorDescription('');
      setNewSubcontractorPrice('');
    } catch (error) {
      console.error('Error adding subcontractor item:', error);
      alert('Failed to add subcontractor item');
    }
  };
  
  // Delete subcontractor item
  const deleteSubcontractorItem = async (itemId: string) => {
    if (!window.confirm('Delete this subcontractor?')) return;
    
    try {
      const { error } = await supabase
        .from('estimate_subcontractors')
        .delete()
        .eq('id', itemId);
      
      if (error) throw error;
      
      await loadSubcontractorItems();
    } catch (error) {
      console.error('Error deleting subcontractor item:', error);
      alert('Failed to delete item');
    }
  };
  
  // Load equipment items
  const loadEquipmentItems = async () => {
    try {
      const { data, error } = await supabase
        .from('estimate_equipment')
        .select('*')
        .eq('estimation_id', estimationId)
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Error loading equipment items:', error);
      } else {
        setEquipmentItems(data || []);
      }
    } catch (error) {
      console.error('Exception loading equipment items:', error);
    }
  };
  
  // Add equipment item
  const addEquipmentItem = async () => {
    if (!newEquipmentName.trim()) {
      alert('Please enter equipment name');
      return;
    }
    
    try {
      const price = parseInput(newEquipmentPrice) || 0;
      
      const { error } = await supabase
        .from('estimate_equipment')
        .insert({
          estimation_id: estimationId,
          name: newEquipmentName,
          description: newEquipmentDescription,
          price: price
        });
      
      if (error) throw error;
      
      console.log('✅ Added equipment item');
      await loadEquipmentItems();
      setShowAddEquipmentModal(false);
      setNewEquipmentName('');
      setNewEquipmentDescription('');
      setNewEquipmentPrice('');
    } catch (error) {
      console.error('Error adding equipment item:', error);
      alert('Failed to add equipment item');
    }
  };
  
  // Delete equipment item
  const deleteEquipmentItem = async (itemId: string) => {
    if (!window.confirm('Delete this equipment?')) return;
    
    try {
      const { error } = await supabase
        .from('estimate_equipment')
        .delete()
        .eq('id', itemId);
      
      if (error) throw error;
      
      await loadEquipmentItems();
    } catch (error) {
      console.error('Error deleting equipment item:', error);
      alert('Failed to delete item');
    }
  };
  
  // Load quoted materials items
  const loadQuotedMaterialsItems = async () => {
    try {
      const { data, error } = await supabase
        .from('estimate_quoted_materials')
        .select('*')
        .eq('estimation_id', estimationId)
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Error loading quoted materials items:', error);
      } else {
        setQuotedMaterialsItems(data || []);
      }
    } catch (error) {
      console.error('Exception loading quoted materials items:', error);
    }
  };
  
  // Add quoted materials item
  const addQuotedMaterialsItem = async () => {
    if (!newQuotedMaterialName.trim()) {
      alert('Please enter material name');
      return;
    }
    
    try {
      const qty = parseInput(newQuotedMaterialQty) || 1;
      const price = parseInput(newQuotedMaterialPrice) || 0;
      
      const { error } = await supabase
        .from('estimate_quoted_materials')
        .insert({
          estimation_id: estimationId,
          name: newQuotedMaterialName,
          description: newQuotedMaterialDescription,
          quantity: qty,
          unit: newQuotedMaterialUnit,
          unit_price: price
        });
      
      if (error) throw error;
      
      console.log('✅ Added quoted materials item');
      await loadQuotedMaterialsItems();
      setShowAddQuotedMaterialsModal(false);
      setNewQuotedMaterialName('');
      setNewQuotedMaterialDescription('');
      setNewQuotedMaterialQty('');
      setNewQuotedMaterialUnit('EA');
      setNewQuotedMaterialPrice('');
    } catch (error) {
      console.error('Error adding quoted materials item:', error);
      alert('Failed to add quoted materials item');
    }
  };
  
  // Delete quoted materials item
  const deleteQuotedMaterialsItem = async (itemId: string) => {
    if (!window.confirm('Delete this quoted material?')) return;
    
    try {
      const { error } = await supabase
        .from('estimate_quoted_materials')
        .delete()
        .eq('id', itemId);
      
      if (error) throw error;
      
      await loadQuotedMaterialsItems();
    } catch (error) {
      console.error('Error deleting quoted materials item:', error);
      alert('Failed to delete item');
    }
  };
  
  // Load ground management items
  const loadGroundManagementItems = async () => {
    try {
      const { data, error } = await supabase
        .from('estimate_ground_management')
        .select('*')
        .eq('estimation_id', estimationId)
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Error loading ground management items:', error);
      } else {
        setGroundManagementItems(data || []);
      }
    } catch (error) {
      console.error('Exception loading ground management items:', error);
    }
  };
  
  // Add ground management item
  const addGroundManagementItem = async () => {
    try {
      const hours = parseInput(newGroundManagementHours) || 0;
      const rate = parseInput(newGroundManagementRate) || laborRate;
      
      const { error } = await supabase
        .from('estimate_ground_management')
        .insert({
          estimation_id: estimationId,
          role: newGroundManagementRole,
          hours: hours,
          hourly_rate: rate
        });
      
      if (error) throw error;
      
      console.log('✅ Added ground management item');
      await loadGroundManagementItems();
      setShowAddGroundManagementModal(false);
      setNewGroundManagementRole('Foreman');
      setNewGroundManagementHours('');
      setNewGroundManagementRate('');
    } catch (error) {
      console.error('Error adding ground management item:', error);
      alert('Failed to add ground management item');
    }
  };
  
  // Delete ground management item
  const deleteGroundManagementItem = async (itemId: string) => {
    if (!window.confirm('Delete this ground management item?')) return;
    
    try {
      const { error } = await supabase
        .from('estimate_ground_management')
        .delete()
        .eq('id', itemId);
      
      if (error) throw error;
      
      await loadGroundManagementItems();
    } catch (error) {
      console.error('Error deleting ground management item:', error);
      alert('Failed to delete item');
    }
  };

  const loadLaborSteps = async () => {
    setLoading(true);
    try {
      // Load global settings and shorthand values from estimations table
      const { data: estimationData, error: estimationError } = await supabase
        .from('estimations')
        .select('labor_rate_per_hour, overhead_percentage, profit_percentage, material_tax_rate, shorthand_material_cost, shorthand_labor_hours, shorthand_pm_labor_rate, shorthand_pm_hours, neca_condition')
        .eq('id', estimationId)
        .single();
      
      if (!estimationError && estimationData) {
        const loadedLaborRate = estimationData.labor_rate_per_hour || 0;
        const loadedOverhead = estimationData.overhead_percentage || 0;
        const loadedProfit = estimationData.profit_percentage || 0;
        const loadedTaxRate = estimationData.material_tax_rate || 0;
        
        setLaborRate(loadedLaborRate);
        setLaborRateInput(formatNumber(loadedLaborRate));
        setOverhead(loadedOverhead);
        setOverheadInput(formatNumber(loadedOverhead));
        setProfit(loadedProfit);
        setProfitInput(formatNumber(loadedProfit));
        setMaterialTaxRate(loadedTaxRate);
        setMaterialTaxRateInput(formatNumber(loadedTaxRate));
        
        // Load NECA condition (default 'normal' if not set)
        setNecaCondition(estimationData.neca_condition ?? 'normal');
        
        const shorthandMat = estimationData.shorthand_material_cost || 0;
        const shorthandLabor = estimationData.shorthand_labor_hours || 0;
        const shorthandPMRate = estimationData.shorthand_pm_labor_rate || 0;
        const shorthandPM = estimationData.shorthand_pm_hours || 0;
        
        setShorthandMaterialCost(shorthandMat);
        setShorthandMaterialCostInput(formatNumber(shorthandMat));
        setShorthandLaborHours(shorthandLabor);
        setShorthandLaborHoursInput(formatNumber(shorthandLabor));
        setShorthandPMLaborRate(shorthandPMRate);
        setShorthandPMLaborRateInput(formatNumber(shorthandPMRate));
        setShorthandPMHours(shorthandPM);
        setShorthandPMHoursInput(formatNumber(shorthandPM));
        console.log('✅ Loaded global settings and shorthand values from database');
      }
      
      const { data, error } = await supabase
        .from('estimate_labor_steps')
        .select('*')
        .eq('estimation_id', estimationId)
        .order('matched_component', { ascending: true })
        .order('measurement', { ascending: true });

      if (error) {
        console.error('Error loading labor steps:', error);
      } else if (data) {
        // Load all steps without merging - each row is unique (Demo vs Install are different)
        const merged: LaborStep[] = [];
        const loadedRowSettings = new Map();
        const loadedEdits = new Map();
        
        const loadedNecaConditions = new Map<string, 'normal' | 'difficult' | 'very_difficult'>();
        const loadedSavedCustomHours = new Map<string, number>();
        
        data.forEach(step => {
          // Use step_description to differentiate Demo vs Install
          const key = `${step.matched_component}-${step.measurement}-${step.step_description || ''}`;
          
          // Each step is its own row - no merging
          merged.push({ ...step });
          
          // Load per-row NECA condition
          if (step.neca_condition) {
            loadedNecaConditions.set(key, step.neca_condition);
          }
          
          // Load row settings from database (if they exist)
          if (step.row_labor_rate || step.row_overhead || step.row_profit) {
            loadedRowSettings.set(key, {
              laborRate: step.row_labor_rate || laborRate,
              overhead: step.row_overhead || overhead,
              profit: step.row_profit || profit
            });
          }
          
          // Load ALL edits from database (quantity, material cost, labor hours)
          const edits: any = {};
          if (step.quantity) edits.quantity = step.quantity;
          if (step.material_cost) edits.materialCost = step.material_cost;
          
          // Handle labor_hours_override
          if (step.labor_hours_override !== null && step.labor_hours_override !== undefined) {
            // Always save to savedCustomHours for restoration
            loadedSavedCustomHours.set(key, step.labor_hours_override);
            
            // Determine if custom is the active mode:
            // If no neca_condition is explicitly set, AND we have a custom override, use custom mode
            // If neca_condition IS set, user wants NECA values (custom is just saved for later)
            const isUsingCustom = !step.neca_condition || step.neca_condition === null;
            
            if (isUsingCustom) {
              edits.laborHours = step.labor_hours_override;
            }
          }
          
          if (Object.keys(edits).length > 0) {
            loadedEdits.set(key, edits);
          }
        });
        
        setLaborSteps(merged);
        setRowSettings(loadedRowSettings);
        setEditedSteps(loadedEdits);
        setRowNecaConditions(loadedNecaConditions);
        setSavedCustomHours(loadedSavedCustomHours);
        console.log('✅ Loaded', merged.length, 'unique components with', loadedNecaConditions.size, 'NECA conditions and', loadedSavedCustomHours.size, 'custom hours');
      }
    } catch (error) {
      console.error('Exception loading labor steps:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get edited value or original
  const getEditedValue = (step: LaborStep, field: 'quantity' | 'materialCost' | 'laborHours') => {
    const key = `${step.matched_component}-${step.measurement}-${step.step_description || ''}`;
    const edited = editedSteps.get(key);
    
    if (field === 'quantity') return edited?.quantity ?? step.quantity;
    if (field === 'materialCost') return edited?.materialCost ?? step.material_cost ?? 0;
    if (field === 'laborHours') return edited?.laborHours;
    return null;
  };

  // Update edited value and save to Supabase
  const updateEditedValue = async (step: LaborStep, field: 'quantity' | 'materialCost' | 'laborHours', value: number) => {
    const key = `${step.matched_component}-${step.measurement}-${step.step_description || ''}`;
    
    // Update local state immediately
    const current = editedSteps.get(key) || {};
    const updated = { ...current, [field]: value };
    const newEditedSteps = new Map(editedSteps);
    newEditedSteps.set(key, updated);
    setEditedSteps(newEditedSteps);
    
    // Save to Supabase in background (don't reload)
    try {
      const updateData: any = {};
      if (field === 'quantity') updateData.quantity = value;
      if (field === 'materialCost') updateData.material_cost = value;
      if (field === 'laborHours') updateData.labor_hours_override = value;
      
      const { error } = await supabase
        .from('estimate_labor_steps')
        .update(updateData)
        .eq('estimation_id', estimationId)
        .eq('matched_component', step.matched_component)
        .eq('measurement', step.measurement);
      
      if (error) {
        console.error('Error saving edit:', error);
        alert('Failed to save changes to database.');
      } else {
        console.log(`✅ Saved ${field} for ${step.matched_component}:`, value);
        
        // NEW: Sync material cost changes to rolling key library
        if (field === 'materialCost') {
          try {
            // Get user_id from estimation
            const { data: estData } = await supabase
              .from('estimations')
              .select('user_id')
              .eq('id', estimationId)
              .single();
            
            if (estData?.user_id) {
              // Use step_description as component name (original name from autocount)
              const componentName = step.step_description || step.matched_component;
              
              const { componentLibraryService } = await import('../services/componentLibraryService');
              const rowCondition = rowNecaConditions.get(key) || 'normal';
              const customHours = editedSteps.get(key)?.laborHours;
              
              await componentLibraryService.syncPricingData(estData.user_id, componentName, {
                material_cost: value,
                labor_unit_type: step.unit as 'E' | 'C' | 'M',
                labor_units: {
                  normal: step.normal_labor_units,
                  difficult: step.difficult_labor_units,
                  very_difficult: step.very_difficult_labor_units
                },
                preferred_labor_condition: rowCondition,
                custom_labor_hours: customHours
              });
              console.log(`💾 Synced all pricing data to library for "${componentName}"`);
            }
          } catch (syncError) {
            console.error('Failed to sync to library (non-critical):', syncError);
          }
        }
      }
    } catch (error) {
      console.error('Exception saving edit:', error);
      alert('Failed to save changes to database.');
    }
  };

  // Calculate labor hours for a step based on per-row NECA condition
  const calculateLaborHours = (step: LaborStep) => {
    const key = `${step.matched_component}-${step.measurement}-${step.step_description || ''}`;
    const edited = editedSteps.get(key);
    
    // If labor hours manually edited, use that
    if (edited?.laborHours !== undefined) {
      return edited.laborHours;
    }
    
    const quantity = edited?.quantity ?? step.quantity;
    
    // Get per-row NECA condition (default to 'normal')
    const rowCondition = rowNecaConditions.get(key) || 'normal';
    
    // Select labor units based on row's condition
    let laborUnits = 0;
    if (rowCondition === 'normal') {
      laborUnits = step.normal_labor_units;
    } else if (rowCondition === 'difficult') {
      laborUnits = step.difficult_labor_units;
    } else {
      laborUnits = step.very_difficult_labor_units;
    }
    
    if (!quantity || !laborUnits) return 0;
    
    const { unit } = step;
    let baseHours = 0;
    if (unit === 'M') baseHours = (quantity / 1000) * laborUnits;
    else if (unit === 'C') baseHours = (quantity / 100) * laborUnits;
    else baseHours = quantity * laborUnits; // E (each)
    
    // Use baseline NECA values directly (no discount applied)
    return baseHours;
  };

  // Calculate totals with per-row settings including PM and misc items
  const getTotals = () => {
    let totalLaborHours = 0;
    let totalLaborCost = 0;
    let totalMaterialCost = 0;
    let totalOverheadAmount = 0;
    let totalProfitAmount = 0;
    let grandTotal = 0;

    laborSteps.forEach(step => {
      const hours = calculateLaborHours(step);
      const key = `${step.matched_component}-${step.measurement}-${step.step_description || ''}`;
      const settings = getRowSettings(key);
      const currentQty = editedSteps.get(key)?.quantity ?? step.quantity;
      const materialUnitCost = editedSteps.get(key)?.materialCost || 0;
      const rowMaterialCost = currentQty * materialUnitCost;
      
      const laborCost = hours * settings.laborRate;
      const subtotal = laborCost + rowMaterialCost;
      const overheadAmount = subtotal * (settings.overhead / 100);
      const profitAmount = (subtotal + overheadAmount) * (settings.profit / 100);
      const rowTotal = subtotal + overheadAmount + profitAmount;
      
      totalLaborHours += hours;
      totalLaborCost += laborCost;
      totalMaterialCost += rowMaterialCost;
      totalOverheadAmount += overheadAmount;
      totalProfitAmount += profitAmount;
      grandTotal += rowTotal;
    });

    const subtotal = totalLaborCost + totalMaterialCost;
    
    // Add PM items total
    const pmTotal = pmItems.reduce((sum, item) => 
      sum + (item.labor_hours * item.pm_hourly_rate), 0
    );
    
    // Add misc items total
    const miscTotal = miscItems.reduce((sum, item) => sum + item.price, 0);
    
    // Add incidental labor total
    const incidentalLaborTotal = incidentalLaborItems.reduce((sum, item) => 
      sum + (item.hours * item.hourly_rate), 0
    );
    
    // Add indirect labor total
    const indirectLaborTotal = indirectLaborItems.reduce((sum, item) => 
      sum + (item.hours * item.hourly_rate), 0
    );
    
    // Add subcontractor total
    const subcontractorTotal = subcontractorItems.reduce((sum, item) => 
      sum + item.price, 0
    );
    
    // Add equipment total
    const equipmentTotal = equipmentItems.reduce((sum, item) => 
      sum + item.price, 0
    );
    
    // Add quoted materials total
    const quotedMaterialsTotal = quotedMaterialsItems.reduce((sum, item) => 
      sum + (item.quantity * item.unit_price), 0
    );
    
    // Add ground management total
    const groundManagementTotal = groundManagementItems.reduce((sum, item) => 
      sum + (item.hours * item.hourly_rate), 0
    );

    return {
      laborHours: totalLaborHours,
      laborCost: totalLaborCost,
      materialCost: totalMaterialCost,
      subtotal,
      overheadAmount: totalOverheadAmount,
      profitAmount: totalProfitAmount,
      total: grandTotal,
      pmTotal,
      miscTotal,
      incidentalLaborTotal,
      indirectLaborTotal,
      subcontractorTotal,
      equipmentTotal,
      quotedMaterialsTotal,
      groundManagementTotal
    };
  };

  const totals = getTotals();
  
  // Calculate shorthand breakdown
  const calculateShorthand = () => {
    const laborCost = shorthandLaborHours * laborRate;
    const materialCostWithTax = shorthandMaterialCost * (1 + materialTaxRate / 100);
    const pmCost = shorthandPMLaborRate * shorthandPMHours;
    const subtotal = laborCost + materialCostWithTax + pmCost;
    const overheadAmount = subtotal * (overhead / 100);
    const profitAmount = (subtotal + overheadAmount) * (profit / 100);
    const total = subtotal + overheadAmount + profitAmount;
    
    return {
      laborCost,
      materialCostWithTax,
      pmCost,
      subtotal,
      overheadAmount,
      profitAmount,
      total
    };
  };
  
  const shorthand = calculateShorthand();

  // Group components by name
  const groupedComponents = laborSteps.reduce((acc, step) => {
    const componentName = step.matched_component.split(',')[0].trim(); // Get base component name
    if (!acc[componentName]) {
      acc[componentName] = [];
    }
    acc[componentName].push(step);
    return acc;
  }, {} as Record<string, LaborStep[]>);

  // Export to CSV with full financial breakdown and per-row settings
  const exportToCSV = () => {
    const rows = [
      // Header
      ['Component & Size', 'Quantity', 'NECA Condition', 'Labor Hours', 'Labor $', 'Material Price', 'Material $', 'Total'],
      
      // Item rows
      ...laborSteps.map(step => {
        const hours = calculateLaborHours(step);
        const key = `${step.matched_component}-${step.measurement}-${step.step_description || ''}`;
        const settings = getRowSettings(key);
        const currentQty = getEditedValue(step, 'quantity') || 0;
        const materialUnitCost = getEditedValue(step, 'materialCost') || 0;
        const totalMaterialCost = currentQty * materialUnitCost;
        
        const rowCondition = rowNecaConditions.get(key) || 'normal';
        const conditionLabel = rowCondition === 'normal' ? 'NECA 1' : rowCondition === 'difficult' ? 'NECA 2' : 'NECA 3';
        
        const laborCost = hours * settings.laborRate;
        const subtotal = laborCost + totalMaterialCost;
        const overheadAmount = subtotal * (settings.overhead / 100);
        const profitAmount = (subtotal + overheadAmount) * (settings.profit / 100);
        const rowTotal = subtotal + overheadAmount + profitAmount;
        
        // Get labor units based on condition
        let laborUnits = 0;
        if (rowCondition === 'normal') {
          laborUnits = step.normal_labor_units;
        } else if (rowCondition === 'difficult') {
          laborUnits = step.difficult_labor_units;
        } else {
          laborUnits = step.very_difficult_labor_units;
        }
        
        return [
          `${step.matched_component} (${step.measurement})`,
          `${formatNumber(currentQty)} ${step.quantity_unit}`,
          editedSteps.get(key)?.laborHours !== undefined ? 'Custom' : `${formatNumber(laborUnits)} ${step.unit}`,
          `${formatNumber(hours)} hrs`,
          `$${formatCurrency(laborCost)}`,
          `$${formatNumber(materialUnitCost)}`,
          `$${formatCurrency(totalMaterialCost)}`,
          `$${formatCurrency(rowTotal)}`
        ];
      }),
      
      // Empty separator
      [],
      
      // Financial breakdown
      ['Subtotal', '', '', '', `$${formatCurrency(totals.laborCost)}`, '', `$${formatCurrency(totals.materialCost)}`, `$${formatCurrency(totals.subtotal)}`],
      [`Material Tax (${materialTaxRate}%)`, '', '', '', '', '', '', `$${formatCurrency(totals.materialCost * (materialTaxRate / 100))}`],
      ['Overhead', '', '', '', '', '', '', `$${formatCurrency(totals.overheadAmount)}`],
      ['Net Profit', '', '', '', '', '', '', `$${formatCurrency(totals.profitAmount)}`],
      [],
      ['ESTIMATED TOTAL', '', '', '', '', '', '', `$${formatCurrency(totals.total + (totals.materialCost * (materialTaxRate / 100)))}`]
    ];

    const escapeCSV = (val: string | number) => `"${String(val).replace(/"/g, '""')}"`;
    const csv = rows.map(row => row.map(escapeCSV).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `estimate-${estimationId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Export to PDF using jsPDF
  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    const currentDate = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    
    const materialTax = totals.materialCost * (materialTaxRate / 100);
    const grandTotal = totals.total + materialTax + totals.pmTotal + totals.miscTotal + 
      totals.incidentalLaborTotal + totals.indirectLaborTotal + 
      totals.subcontractorTotal + totals.equipmentTotal + totals.quotedMaterialsTotal +
      totals.groundManagementTotal;

    // Header - Job Name (left) and Date (right) - same style as table cells
    const displayName = projectName || 'Untitled Project';
    doc.setFontSize(9);
    doc.setTextColor(31, 41, 55);
    doc.text(`Job Name - ${displayName}`, 40, 40);
    doc.text(currentDate, pageWidth - 40, 40, { align: 'right' });

    // Common table styles
    const tableHeadStyles = {
      fillColor: [240, 240, 240] as [number, number, number],
      textColor: [31, 41, 55] as [number, number, number],
      fontStyle: 'bold' as const,
      fontSize: 9,
      lineWidth: 0.5,
      lineColor: [0, 0, 0] as [number, number, number]
    };
    const tableBodyStyles = {
      fontSize: 9,
      textColor: [31, 41, 55] as [number, number, number],
      lineWidth: 0.5,
      lineColor: [0, 0, 0] as [number, number, number]
    };

    // Helper function to add a new page if needed
    const checkPageBreak = (currentY: number, neededSpace: number = 100) => {
      if (currentY + neededSpace > pageHeight - 60) {
        doc.addPage();
        return 40;
      }
      return currentY;
    };

    // ============ MAIN MATERIALS TABLE ============
    const mainTableData: string[][] = [];
    laborSteps.forEach(step => {
      const hours = calculateLaborHours(step);
      const key = `${step.matched_component}-${step.measurement}-${step.step_description || ''}`;
      const settings = getRowSettings(key);
      const currentQty = getEditedValue(step, 'quantity') || 0;
      const materialUnitCost = getEditedValue(step, 'materialCost') || 0;
      const totalMaterialCost = currentQty * materialUnitCost;
      const laborCost = hours * settings.laborRate;
      const subtotal = laborCost + totalMaterialCost;
      const overheadAmount = subtotal * (settings.overhead / 100);
      const profitAmount = (subtotal + overheadAmount) * (settings.profit / 100);
      const rowTotal = subtotal + overheadAmount + profitAmount;
      
      mainTableData.push([
        `${step.matched_component} - ${step.measurement}${step.step_description ? ` - ${step.step_description}` : ''}`,
        formatNumber(currentQty),
        step.quantity_unit || 'EA',
        `${formatNumber(hours)} hrs`,
        `$${formatCurrency(materialUnitCost)}`,
        `$${formatCurrency(rowTotal)}`
      ]);
    });

    // Footer styles for totals rows
    const tableFootStyles = {
      fillColor: [240, 240, 240] as [number, number, number],
      textColor: [31, 41, 55] as [number, number, number],
      fontStyle: 'bold' as const,
      fontSize: 9,
      lineWidth: 0.5,
      lineColor: [0, 0, 0] as [number, number, number]
    };

    autoTable(doc, {
      startY: 55,
      head: [['Description', 'Qty', 'Labor Units', 'Labor Hrs', 'Labor $', 'Material Price', 'Material $', 'Total']],
      body: mainTableData,
      foot: [['MATERIALS TOTAL', '', '', `${formatNumber(totals.laborHours)} hrs`, `$${formatCurrency(totals.laborCost)}`, '', `$${formatCurrency(totals.materialCost)}`, `$${formatCurrency(totals.total + (totals.materialCost * (materialTaxRate / 100)))}`]],
      theme: 'grid',
      headStyles: tableHeadStyles,
      bodyStyles: tableBodyStyles,
      footStyles: tableFootStyles,
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 40, halign: 'center' },
        2: { cellWidth: 35, halign: 'center' },
        3: { cellWidth: 50, halign: 'center' },
        4: { cellWidth: 65, halign: 'right' },
        5: { cellWidth: 65, halign: 'right' }
      },
      margin: { left: 40, right: 40 }
    });

    let y = (doc as any).lastAutoTable.finalY + 25;

    // ============ GROUND MANAGEMENT TABLE ============
    if (groundManagementItems.length > 0) {
      y = checkPageBreak(y);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Ground Management', 40, y);
      y += 5;
      
      const groundMgmtData = groundManagementItems.map(item => [
        item.role,
        `${formatNumber(item.hours)} hrs`,
        `$${formatCurrency(item.hourly_rate)}/hr`,
        `$${formatCurrency(item.hours * item.hourly_rate)}`
      ]);
      
      autoTable(doc, {
        startY: y,
        head: [['Role', 'Hours', 'Rate', 'Total']],
        body: groundMgmtData,
        foot: [['TOTAL', '', '', `$${formatCurrency(totals.groundManagementTotal)}`]],
        theme: 'grid',
        headStyles: tableHeadStyles,
        bodyStyles: tableBodyStyles,
        footStyles: tableFootStyles,
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 70, halign: 'center' },
          2: { cellWidth: 80, halign: 'right' },
          3: { cellWidth: 80, halign: 'right' }
        },
        margin: { left: 40, right: 40 }
      });
      y = (doc as any).lastAutoTable.finalY + 25;
    }

    // ============ INCIDENTAL LABOR TABLE ============
    if (incidentalLaborItems.length > 0) {
      y = checkPageBreak(y);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Incidental Labor', 40, y);
      y += 5;
      
      const incidentalData = incidentalLaborItems.map(item => [
        item.description,
        `${formatNumber(item.hours)} hrs`,
        `$${formatCurrency(item.hourly_rate)}/hr`,
        `$${formatCurrency(item.hours * item.hourly_rate)}`
      ]);
      
      autoTable(doc, {
        startY: y,
        head: [['Description', 'Hours', 'Rate', 'Total']],
        body: incidentalData,
        foot: [['TOTAL', '', '', `$${formatCurrency(totals.incidentalLaborTotal)}`]],
        theme: 'grid',
        headStyles: tableHeadStyles,
        bodyStyles: tableBodyStyles,
        footStyles: tableFootStyles,
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 70, halign: 'center' },
          2: { cellWidth: 80, halign: 'right' },
          3: { cellWidth: 80, halign: 'right' }
        },
        margin: { left: 40, right: 40 }
      });
      y = (doc as any).lastAutoTable.finalY + 25;
    }

    // ============ INDIRECT LABOR TABLE ============
    if (indirectLaborItems.length > 0) {
      y = checkPageBreak(y);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Indirect Labor', 40, y);
      y += 5;
      
      const indirectData = indirectLaborItems.map(item => [
        item.description,
        `${formatNumber(item.hours)} hrs`,
        `$${formatCurrency(item.hourly_rate)}/hr`,
        `$${formatCurrency(item.hours * item.hourly_rate)}`
      ]);
      
      autoTable(doc, {
        startY: y,
        head: [['Description', 'Hours', 'Rate', 'Total']],
        body: indirectData,
        foot: [['TOTAL', '', '', `$${formatCurrency(totals.indirectLaborTotal)}`]],
        theme: 'grid',
        headStyles: tableHeadStyles,
        bodyStyles: tableBodyStyles,
        footStyles: tableFootStyles,
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 70, halign: 'center' },
          2: { cellWidth: 80, halign: 'right' },
          3: { cellWidth: 80, halign: 'right' }
        },
        margin: { left: 40, right: 40 }
      });
      y = (doc as any).lastAutoTable.finalY + 25;
    }

    // ============ SUBCONTRACTORS TABLE ============
    if (subcontractorItems.length > 0) {
      y = checkPageBreak(y);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Subcontractors', 40, y);
      y += 5;
      
      const subData = subcontractorItems.map(item => [
        item.name,
        item.description || '-',
        `$${formatCurrency(item.price)}`
      ]);
      
      autoTable(doc, {
        startY: y,
        head: [['Subcontractor', 'Description', 'Price']],
        body: subData,
        foot: [['TOTAL', '', `$${formatCurrency(totals.subcontractorTotal)}`]],
        theme: 'grid',
        headStyles: tableHeadStyles,
        bodyStyles: tableBodyStyles,
        footStyles: tableFootStyles,
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 80, halign: 'right' }
        },
        margin: { left: 40, right: 40 }
      });
      y = (doc as any).lastAutoTable.finalY + 25;
    }

    // ============ EQUIPMENT TABLE ============
    if (equipmentItems.length > 0) {
      y = checkPageBreak(y);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Equipment', 40, y);
      y += 5;
      
      const equipData = equipmentItems.map(item => [
        item.name,
        item.description || '-',
        `$${formatCurrency(item.price)}`
      ]);
      
      autoTable(doc, {
        startY: y,
        head: [['Equipment', 'Description', 'Price']],
        body: equipData,
        foot: [['TOTAL', '', `$${formatCurrency(totals.equipmentTotal)}`]],
        theme: 'grid',
        headStyles: tableHeadStyles,
        bodyStyles: tableBodyStyles,
        footStyles: tableFootStyles,
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 80, halign: 'right' }
        },
        margin: { left: 40, right: 40 }
      });
      y = (doc as any).lastAutoTable.finalY + 25;
    }

    // ============ QUOTED MATERIALS TABLE ============
    if (quotedMaterialsItems.length > 0) {
      y = checkPageBreak(y);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Quoted Materials', 40, y);
      y += 5;
      
      const quotedData = quotedMaterialsItems.map(item => [
        item.name + (item.description ? ` - ${item.description}` : ''),
        formatNumber(item.quantity),
        item.unit || 'EA',
        `$${formatCurrency(item.unit_price)}`,
        `$${formatCurrency(item.quantity * item.unit_price)}`
      ]);
      
      autoTable(doc, {
        startY: y,
        head: [['Material', 'Qty', 'Unit', 'Unit Price', 'Total']],
        body: quotedData,
        foot: [['TOTAL', '', '', '', `$${formatCurrency(totals.quotedMaterialsTotal)}`]],
        theme: 'grid',
        headStyles: tableHeadStyles,
        bodyStyles: tableBodyStyles,
        footStyles: tableFootStyles,
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 50, halign: 'center' },
          2: { cellWidth: 40, halign: 'center' },
          3: { cellWidth: 70, halign: 'right' },
          4: { cellWidth: 70, halign: 'right' }
        },
        margin: { left: 40, right: 40 }
      });
      y = (doc as any).lastAutoTable.finalY + 25;
    }

    // ============ MISCELLANEOUS TABLE ============
    if (miscItems.length > 0) {
      y = checkPageBreak(y);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Miscellaneous', 40, y);
      y += 5;
      
      const miscData = miscItems.map(item => [
        item.item_name || item.name,
        item.description || '-',
        `$${formatCurrency(item.price)}`
      ]);
      
      autoTable(doc, {
        startY: y,
        head: [['Item', 'Description', 'Price']],
        body: miscData,
        foot: [['TOTAL', '', `$${formatCurrency(totals.miscTotal)}`]],
        theme: 'grid',
        headStyles: tableHeadStyles,
        bodyStyles: tableBodyStyles,
        footStyles: tableFootStyles,
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 80, halign: 'right' }
        },
        margin: { left: 40, right: 40 }
      });
      y = (doc as any).lastAutoTable.finalY + 25;
    }

    // ============ PROJECT MANAGEMENT TABLE ============
    if (pmItems.length > 0) {
      y = checkPageBreak(y);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Project Management', 40, y);
      y += 5;
      
      const pmData = pmItems.map(item => [
        item.item_name || 'Project Management Services',
        `${formatNumber(item.labor_hours)} hrs`,
        `$${formatCurrency(item.pm_hourly_rate)}/hr`,
        `$${formatCurrency(item.labor_hours * item.pm_hourly_rate)}`
      ]);
      
      autoTable(doc, {
        startY: y,
        head: [['Description', 'Hours', 'Rate', 'Total']],
        body: pmData,
        foot: [['TOTAL', '', '', `$${formatCurrency(totals.pmTotal)}`]],
        theme: 'grid',
        headStyles: tableHeadStyles,
        bodyStyles: tableBodyStyles,
        footStyles: tableFootStyles,
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 70, halign: 'center' },
          2: { cellWidth: 80, halign: 'right' },
          3: { cellWidth: 80, halign: 'right' }
        },
        margin: { left: 40, right: 40 }
      });
      y = (doc as any).lastAutoTable.finalY + 25;
    }

    // Check for page break before summary
    y = checkPageBreak(y, 150);

    // ============ SUMMARY BREAKDOWN ============
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary', 40, y);
    y += 15;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(31, 41, 55);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    
    const addSummaryRow = (label: string, value: string) => {
      doc.text(label, 40, y);
      doc.text(value, pageWidth - 40, y, { align: 'right' });
      y += 14;
    };

    addSummaryRow(`Labor (${formatNumber(totals.laborHours)} hrs)`, `$${formatCurrency(totals.laborCost)}`);
    addSummaryRow('Materials', `$${formatCurrency(totals.materialCost)}`);
    addSummaryRow(`Material Tax (${materialTaxRate}%)`, `$${formatCurrency(materialTax)}`);
    
    if (totals.incidentalLaborTotal > 0) {
      addSummaryRow('Incidental Labor', `$${formatCurrency(totals.incidentalLaborTotal)}`);
    }
    if (totals.indirectLaborTotal > 0) {
      addSummaryRow('Indirect Labor', `$${formatCurrency(totals.indirectLaborTotal)}`);
    }
    if (totals.subcontractorTotal > 0) {
      addSummaryRow('Subcontractors', `$${formatCurrency(totals.subcontractorTotal)}`);
    }
    if (totals.equipmentTotal > 0) {
      addSummaryRow('Equipment', `$${formatCurrency(totals.equipmentTotal)}`);
    }
    if (totals.quotedMaterialsTotal > 0) {
      addSummaryRow('Quoted Materials', `$${formatCurrency(totals.quotedMaterialsTotal)}`);
    }
    if (totals.groundManagementTotal > 0) {
      addSummaryRow('Ground Management', `$${formatCurrency(totals.groundManagementTotal)}`);
    }
    if (totals.pmTotal > 0) {
      addSummaryRow('Project Management', `$${formatCurrency(totals.pmTotal)}`);
    }
    if (totals.miscTotal > 0) {
      addSummaryRow('Miscellaneous', `$${formatCurrency(totals.miscTotal)}`);
    }
    
    addSummaryRow(`Overhead (${overhead}%)`, `$${formatCurrency(totals.overheadAmount)}`);
    addSummaryRow(`Profit (${profit}%)`, `$${formatCurrency(totals.profitAmount)}`);
    
    // Line before total
    y += 2;
    doc.line(40, y, pageWidth - 40, y);
    y += 12;
    
    // Final total row (bold)
    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL', 40, y);
    doc.text(`$${formatCurrency(grandTotal)}`, pageWidth - 40, y, { align: 'right' });
    
    // Line after total
    y += 5;
    doc.line(40, y, pageWidth - 40, y);
    doc.setFont('helvetica', 'normal');

    // Save with project name in filename
    const safeFileName = (projectName || 'estimate').replace(/[^a-zA-Z0-9]/g, '_').slice(0, 30);
    doc.save(`${safeFileName}_estimate.pdf`);
  };

  // Export Change Order PDF
  const exportChangeOrder = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    const currentDate = new Date().toLocaleDateString('en-US', { 
      month: 'numeric',
      day: 'numeric', 
      year: 'numeric'
    });
    
    const materialTax = totals.materialCost * (materialTaxRate / 100);
    const totalMaterial = totals.materialCost + materialTax;
    const laborCostTotal = totals.laborHours * laborRate;
    const subtotal1 = totalMaterial + laborCostTotal;
    const overheadAmount = subtotal1 * (overhead / 100);
    const profitAmount = subtotal1 * (profit / 100);
    const subtotal2 = subtotal1 + overheadAmount + profitAmount;
    const finalAmount = subtotal2;

    // Use CCN from modal input
    const ccnNumber = changeOrderCCN || estimationId.slice(0, 5).toUpperCase();

    let y = 50;

    // CHANGE NOTICE Header (centered, bold)
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('CHANGE NOTICE', pageWidth / 2, y, { align: 'center' });
    
    // Right side info block
    y = 70;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    const labelX = pageWidth - 180;
    const valueX = pageWidth - 100;
    
    doc.text('CCN #', labelX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(ccnNumber, valueX, y);
    
    y += 12;
    doc.setFont('helvetica', 'bold');
    doc.text('Date:', labelX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(currentDate, valueX, y);
    
    y += 12;
    doc.setFont('helvetica', 'bold');
    doc.text('Project Name:', labelX, y);
    doc.setFont('helvetica', 'normal');
    const projName = (projectName || 'Untitled').slice(0, 25);
    doc.text(projName, valueX, y);
    
    y += 12;
    doc.setFont('helvetica', 'bold');
    doc.text('Project Number:', labelX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(projName, valueX, y);
    
    y += 12;
    doc.setFont('helvetica', 'bold');
    doc.text('Page Number:', labelX, y);
    doc.setFont('helvetica', 'normal');
    doc.text('1', valueX, y);

    // Client Address
    y = 140;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Client Address:', 40, y);
    if (changeOrderClientAddress) {
      doc.text(changeOrderClientAddress, 115, y);
    } else {
      doc.line(110, y + 2, 300, y + 2);
    }

    // Work Description section
    y = 175;
    doc.setFont('helvetica', 'bold');
    doc.text('Work Description', 40, y);
    doc.line(40, y + 3, 130, y + 3);
    
    // Work description box
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.rect(40, y + 10, pageWidth - 80, 40);
    
    // Add work description text if provided
    if (changeOrderWorkDescription) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      const splitText = doc.splitTextToSize(changeOrderWorkDescription, pageWidth - 100);
      doc.text(splitText, 50, y + 22);
    }

    // Itemized Breakdown section
    y = 240;
    doc.setFont('helvetica', 'bold');
    doc.text('Itemized Breakdown', 40, y);
    doc.line(40, y + 3, 140, y + 3);

    // Build table data for change order
    const changeOrderData: string[][] = [];
    let totalQty = 0;
    let totalMatCost = 0;
    let totalHrs = 0;
    
    laborSteps.forEach(step => {
      const hours = calculateLaborHours(step);
      const currentQty = getEditedValue(step, 'quantity') || 0;
      const materialUnitCost = getEditedValue(step, 'materialCost') || 0;
      const rowMaterialTotal = currentQty * materialUnitCost;
      
      // Determine unit suffix (M=per 1000, C=per 100, E=each)
      let priceSuffix = 'E';
      let laborSuffix = 'E';
      if (currentQty >= 100) {
        priceSuffix = 'C';
        laborSuffix = 'C';
      }
      if (currentQty >= 1000) {
        priceSuffix = 'M';
        laborSuffix = 'M';
      }
      
      totalQty += currentQty;
      totalMatCost += rowMaterialTotal;
      totalHrs += hours;
      
      changeOrderData.push([
        `${step.matched_component} ${step.measurement}`.slice(0, 40),
        formatNumber(currentQty),
        `${formatNumber(materialUnitCost)} ${priceSuffix}`,
        formatNumber(rowMaterialTotal),
        `${formatNumber(hours)} ${laborSuffix}`,
        formatNumber(hours)
      ]);
    });
    
    // Add incidental labor items to change order
    incidentalLaborItems.forEach(item => {
      totalHrs += item.hours;
      changeOrderData.push([
        `Incidental: ${item.description}`.slice(0, 40),
        '1',
        '-',
        '-',
        `${formatNumber(item.hours)} E`,
        formatNumber(item.hours)
      ]);
    });
    
    // Add indirect labor items to change order
    indirectLaborItems.forEach(item => {
      totalHrs += item.hours;
      changeOrderData.push([
        `Indirect: ${item.description}`.slice(0, 40),
        '1',
        '-',
        '-',
        `${formatNumber(item.hours)} E`,
        formatNumber(item.hours)
      ]);
    });
    
    // Add subcontractor items to change order
    subcontractorItems.forEach(item => {
      changeOrderData.push([
        `Sub: ${item.name}`.slice(0, 40),
        '1',
        formatNumber(item.price),
        formatNumber(item.price),
        '-',
        '-'
      ]);
      totalMatCost += item.price;
    });
    
    // Add equipment items to change order
    equipmentItems.forEach(item => {
      changeOrderData.push([
        `Equipment: ${item.name}`.slice(0, 40),
        '1',
        formatNumber(item.price),
        formatNumber(item.price),
        '-',
        '-'
      ]);
      totalMatCost += item.price;
    });
    
    // Add quoted materials to change order
    quotedMaterialsItems.forEach(item => {
      changeOrderData.push([
        `Quoted: ${item.name}`.slice(0, 40),
        formatNumber(item.quantity),
        formatNumber(item.unit_price),
        formatNumber(item.quantity * item.unit_price),
        '-',
        '-'
      ]);
      totalQty += item.quantity;
      totalMatCost += item.quantity * item.unit_price;
    });
    
    // Add ground management to change order
    groundManagementItems.forEach(item => {
      totalHrs += item.hours;
      changeOrderData.push([
        `${item.role}`.slice(0, 40),
        '1',
        '-',
        '-',
        `${formatNumber(item.hours)} E`,
        formatNumber(item.hours)
      ]);
    });

    // Create itemized breakdown table with grid
    autoTable(doc, {
      startY: y + 10,
      head: [['Description', 'Qty', 'Net Price U', 'Total Mat.', 'Labor U', 'Total Hrs.']],
      body: changeOrderData,
      foot: [['Totals', formatNumber(totalQty), '', formatNumber(totalMatCost), '', formatNumber(totalHrs)]],
      theme: 'grid',
      headStyles: {
        fillColor: [240, 240, 240],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 8,
        lineWidth: 0.5,
        lineColor: [0, 0, 0]
      },
      bodyStyles: {
        fontSize: 8,
        textColor: [0, 0, 0],
        lineWidth: 0.5,
        lineColor: [0, 0, 0]
      },
      footStyles: {
        fillColor: [240, 240, 240],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 8,
        lineWidth: 0.5,
        lineColor: [0, 0, 0]
      },
      columnStyles: {
        0: { cellWidth: 'auto', halign: 'left' },
        1: { cellWidth: 45, halign: 'right' },
        2: { cellWidth: 60, halign: 'right' },
        3: { cellWidth: 55, halign: 'right' },
        4: { cellWidth: 50, halign: 'right' },
        5: { cellWidth: 50, halign: 'right' }
      },
      margin: { left: 40, right: 40 },
      showFoot: 'lastPage'
    });

    // Get Y position after table
    let summaryY = (doc as any).lastAutoTable.finalY + 20;

    // Summary section
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Summary', 40, summaryY);
    doc.line(40, summaryY + 3, 80, summaryY + 3);
    
    summaryY += 18;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    
    const addSummaryLine = (label: string, detail: string, value: string, indent: number = 0) => {
      doc.text(label, 40 + indent, summaryY);
      if (detail) {
        doc.text(detail, 150, summaryY);
      }
      doc.text(value, pageWidth - 60, summaryY, { align: 'right' });
      summaryY += 12;
    };

    addSummaryLine('General Materials', '', formatNumber(totals.materialCost));
    addSummaryLine('Material Tax', `(@ ${formatNumber(materialTaxRate)} %)`, formatNumber(materialTax));
    
    // Line positions: right edge aligns with numbers end, left edge 5px past where numbers start
    const lineRight = pageWidth - 60;  // Where numbers end (right-aligned position)
    const lineLeft = pageWidth - 145;  // 5px left of where numbers start
    
    // Line after Material Tax (right under the number)
    doc.setLineWidth(1);
    doc.line(lineLeft, summaryY - 8, lineRight, summaryY - 8);
    summaryY += 4;
    doc.setFont('helvetica', 'bold');
    addSummaryLine('Total Material', '', formatNumber(totalMaterial));
    doc.setFont('helvetica', 'normal');
    addSummaryLine('FOREMAN', `(${formatNumber(totals.laborHours)} Hrs @ $${formatNumber(laborRate)})`, formatNumber(laborCostTotal));
    
    // Line after FOREMAN (right under the number)
    doc.line(lineLeft, summaryY - 8, lineRight, summaryY - 8);
    summaryY += 4;
    doc.setFont('helvetica', 'bold');
    addSummaryLine('Subtotal', '', formatNumber(subtotal1));
    doc.setFont('helvetica', 'normal');
    addSummaryLine('Overhead', `(@ ${formatNumber(overhead)} %)`, formatNumber(overheadAmount));
    addSummaryLine('Markup', `(@ ${formatNumber(profit)} %)`, formatNumber(profitAmount));
    
    // Line after Markup (right under the number)
    doc.line(lineLeft, summaryY - 8, lineRight, summaryY - 8);
    summaryY += 4;
    doc.setFont('helvetica', 'bold');
    addSummaryLine('Subtotal', '', formatNumber(subtotal2));
    
    // Line after second Subtotal (right under the number)
    doc.line(lineLeft, summaryY - 8, lineRight, summaryY - 8);
    summaryY += 6;
    doc.setFontSize(10);
    addSummaryLine('Final Amount', '', `$${formatCurrency(finalAmount)}`);

    // ============ PAGE 2 ============
    doc.addPage();
    
    // Page 2 Header - same as page 1 but with page number 2
    let p2y = 50;
    
    // CHANGE NOTICE Header (top left)
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('CHANGE NOTICE', 40, p2y);
    
    // Client Address (middle area)
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Client Address:', pageWidth / 2 - 80, p2y);
    doc.line(pageWidth / 2, p2y + 2, pageWidth / 2 + 100, p2y + 2);
    
    // Right side info block
    p2y = 35;
    doc.setFontSize(9);
    const p2LabelX = pageWidth - 180;
    const p2ValueX = pageWidth - 100;
    
    doc.setFont('helvetica', 'bold');
    doc.text('CCN #', p2LabelX, p2y);
    doc.setFont('helvetica', 'normal');
    doc.text(ccnNumber, p2ValueX, p2y);
    
    p2y += 12;
    doc.setFont('helvetica', 'bold');
    doc.text('Date:', p2LabelX, p2y);
    doc.setFont('helvetica', 'normal');
    doc.text(currentDate, p2ValueX, p2y);
    
    p2y += 12;
    doc.setFont('helvetica', 'bold');
    doc.text('Project Name:', p2LabelX, p2y);
    doc.setFont('helvetica', 'normal');
    doc.text((projectName || 'Untitled').slice(0, 25), p2ValueX, p2y);
    
    p2y += 12;
    doc.setFont('helvetica', 'bold');
    doc.text('Project Number:', p2LabelX, p2y);
    doc.setFont('helvetica', 'normal');
    doc.text((projectName || 'Untitled').slice(0, 25), p2ValueX, p2y);
    
    p2y += 12;
    doc.setFont('helvetica', 'bold');
    doc.text('Page Number:', p2LabelX, p2y);
    doc.setFont('helvetica', 'normal');
    doc.text('2', p2ValueX, p2y);

    // ============ CONTRACTOR CERTIFICATION BOX ============
    p2y = 100;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(1);
    doc.rect(40, p2y, pageWidth - 80, 120);
    
    // Box contents
    let boxY = p2y + 25;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    
    doc.text('Name:', 55, boxY);
    doc.setLineWidth(0.5);
    doc.line(110, boxY + 2, pageWidth - 60, boxY + 2);
    
    boxY += 25;
    doc.text('Date:', 55, boxY);
    doc.line(110, boxY + 2, pageWidth - 60, boxY + 2);
    
    boxY += 25;
    doc.text('Signature:', 55, boxY);
    doc.line(110, boxY + 2, pageWidth - 60, boxY + 2);
    
    // Certification text at bottom of box
    boxY += 30;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('I hereby certify that this quotation is complete and accurate based on the information provided.', 55, boxY);

    // ============ CLIENT ACCEPTANCE SECTION ============
    p2y = 240;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('CLIENT ACCEPTANCE', 40, p2y);
    doc.setLineWidth(1);
    doc.line(40, p2y + 3, 160, p2y + 3);

    // ============ CLIENT ACCEPTANCE BOX ============
    p2y = 260;
    doc.setLineWidth(1);
    doc.rect(40, p2y, pageWidth - 80, 180);
    
    // Box contents
    boxY = p2y + 25;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    
    doc.text('CCN #', 55, boxY);
    doc.setFont('helvetica', 'normal');
    doc.text(ccnNumber, 140, boxY);
    
    boxY += 18;
    doc.setFont('helvetica', 'bold');
    doc.text('Final Amount:', 55, boxY);
    doc.setFont('helvetica', 'normal');
    doc.text(`$${formatCurrency(finalAmount)}`, 140, boxY);
    
    boxY += 25;
    doc.setFont('helvetica', 'bold');
    doc.text('Name:', 55, boxY);
    doc.setLineWidth(0.5);
    doc.line(110, boxY + 2, pageWidth - 60, boxY + 2);
    
    boxY += 22;
    doc.text('Date:', 55, boxY);
    doc.line(110, boxY + 2, pageWidth - 60, boxY + 2);
    
    boxY += 22;
    doc.text('Signature:', 55, boxY);
    doc.line(110, boxY + 2, pageWidth - 60, boxY + 2);
    
    boxY += 22;
    doc.text('Change Order #:', 55, boxY);
    doc.line(140, boxY + 2, pageWidth - 60, boxY + 2);
    
    // Acceptance text at bottom of box
    boxY += 25;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('I hereby accept this quotation and authorize the contractor to complete the above described work.', 55, boxY);

    // ============ ORIGINAL FOOTER ============
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('ORIGINAL', pageWidth / 2, pageHeight - 40, { align: 'center' });

    // Save
    const safeFileName = (projectName || 'change_order').replace(/[^a-zA-Z0-9]/g, '_').slice(0, 30);
    doc.save(`${safeFileName}_change_order.pdf`);
  };

  if (loading) {
    return (
      <div className="estimate-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="loading-spinner" style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid var(--border-color)',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }} />
          <p style={{ color: 'var(--text-muted)' }}>Loading estimate...</p>
        </div>
      </div>
    );
  }

  // Render settings and shorthand even when no labor steps
  const renderSettingsAndShorthand = () => (
    <>
      {/* Settings Cards */}
      <div className="estimate-settings">
        <div className="settings-card-grid">
          <div className="setting-card blue-accent">
            <div className="setting-card-label">
              <Clock size={14} />
              LABOR RATE
            </div>
            <div className="setting-card-value-wrapper">
              <span className="input-prefix">$</span>
              <input
                type="text"
                className="setting-card-input"
                value={laborRateInput}
                onChange={(e) => setLaborRateInput(e.target.value)}
                onBlur={async () => {
                  const parsed = parseInput(laborRateInput) || 0;
                  const formatted = formatNumber(parsed);
                  setLaborRate(parsed);
                  setLaborRateInput(formatted);
                  try {
                    await supabase
                      .from('estimations')
                      .update({ labor_rate_per_hour: parsed })
                      .eq('id', estimationId);
                    console.log('✅ Saved labor rate to database');
                  } catch (error) {
                    console.error('Error saving labor rate:', error);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
              />
            </div>
          </div>
          
          <div className="setting-card orange-accent">
            <div className="setting-card-label">
              <Layers size={14} />
              OVERHEAD
            </div>
            <div className="setting-card-value-wrapper">
              <input
                type="text"
                className="setting-card-input"
                value={overheadInput}
                onChange={(e) => setOverheadInput(e.target.value)}
                onBlur={async () => {
                  const parsed = parseInput(overheadInput) || 0;
                  const formatted = formatNumber(parsed);
                  setOverhead(parsed);
                  setOverheadInput(formatted);
                  try {
                    await supabase
                      .from('estimations')
                      .update({ overhead_percentage: parsed })
                      .eq('id', estimationId);
                    console.log('✅ Saved overhead to database');
                  } catch (error) {
                    console.error('Error saving overhead:', error);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
              />
              <span className="input-suffix">%</span>
            </div>
          </div>
          
          <div className="setting-card green-accent">
            <div className="setting-card-label">
              <DollarSign size={14} />
              PROFIT MARGIN
            </div>
            <div className="setting-card-value-wrapper">
              <input
                type="text"
                className="setting-card-input"
                value={profitInput}
                onChange={(e) => setProfitInput(e.target.value)}
                onBlur={async () => {
                  const parsed = parseInput(profitInput) || 0;
                  const formatted = formatNumber(parsed);
                  setProfit(parsed);
                  setProfitInput(formatted);
                  try {
                    await supabase
                      .from('estimations')
                      .update({ profit_percentage: parsed })
                      .eq('id', estimationId);
                    console.log('✅ Saved profit to database');
                  } catch (error) {
                    console.error('Error saving profit:', error);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
              />
              <span className="input-suffix">%</span>
            </div>
          </div>
          
          <div className="setting-card purple-accent">
            <div className="setting-card-label">
              <Receipt size={14} />
              MATERIAL TAX
            </div>
            <div className="setting-card-value-wrapper">
              <input
                type="text"
                className="setting-card-input"
                value={materialTaxRateInput}
                onChange={(e) => setMaterialTaxRateInput(e.target.value)}
                onBlur={async () => {
                  const parsed = parseInput(materialTaxRateInput) || 0;
                  const formatted = formatNumber(parsed);
                  setMaterialTaxRate(parsed);
                  setMaterialTaxRateInput(formatted);
                  try {
                    await supabase
                      .from('estimations')
                      .update({ material_tax_rate: parsed })
                      .eq('id', estimationId);
                    console.log('✅ Saved material tax rate to database');
                  } catch (error) {
                    console.error('Error saving material tax rate:', error);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
              />
              <span className="input-suffix">%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Shorthand Estimate */}
      <div className="shorthand-section">
        <div className="shorthand-card">
          <h3 className="shorthand-title">SHORTHAND ESTIMATE</h3>
          <div className="shorthand-inputs-grid">
            <div className="shorthand-input-card">
              <div className="shorthand-input-label">Estimated Material Costs</div>
              <div className="shorthand-input-wrapper">
                <span className="input-prefix">$</span>
                <input
                  type="text"
                  className="shorthand-input"
                  value={shorthandMaterialCostInput}
                  onChange={(e) => setShorthandMaterialCostInput(e.target.value)}
                  onBlur={async () => {
                    const parsed = parseInput(shorthandMaterialCostInput) || 0;
                    const formatted = formatNumber(parsed);
                    setShorthandMaterialCost(parsed);
                    setShorthandMaterialCostInput(formatted);
                    try {
                      await supabase
                        .from('estimations')
                        .update({ shorthand_material_cost: parsed })
                        .eq('id', estimationId);
                      console.log('✅ Saved shorthand material cost');
                    } catch (error) {
                      console.error('Error saving shorthand material cost:', error);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                  }}
                />
              </div>
            </div>
            
            <div className="shorthand-input-card">
              <div className="shorthand-input-label">Estimated Labor Hours</div>
              <div className="shorthand-input-wrapper">
                <input
                  type="text"
                  className="shorthand-input"
                  value={shorthandLaborHoursInput}
                  onChange={(e) => setShorthandLaborHoursInput(e.target.value)}
                  onBlur={async () => {
                    const parsed = parseInput(shorthandLaborHoursInput) || 0;
                    const formatted = formatNumber(parsed);
                    setShorthandLaborHours(parsed);
                    setShorthandLaborHoursInput(formatted);
                    try {
                      await supabase
                        .from('estimations')
                        .update({ shorthand_labor_hours: parsed })
                        .eq('id', estimationId);
                      console.log('✅ Saved shorthand labor hours');
                    } catch (error) {
                      console.error('Error saving shorthand labor hours:', error);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                  }}
                />
              </div>
            </div>
            
            <div className="shorthand-input-card">
              <div className="shorthand-input-label">PM Labor Rate</div>
              <div className="shorthand-input-wrapper">
                <span className="input-prefix">$</span>
                <input
                  type="text"
                  className="shorthand-input"
                  value={shorthandPMLaborRateInput}
                  onChange={(e) => setShorthandPMLaborRateInput(e.target.value)}
                  onBlur={async () => {
                    const parsed = parseInput(shorthandPMLaborRateInput) || 0;
                    const formatted = formatNumber(parsed);
                    setShorthandPMLaborRate(parsed);
                    setShorthandPMLaborRateInput(formatted);
                    try {
                      await supabase
                        .from('estimations')
                        .update({ shorthand_pm_labor_rate: parsed })
                        .eq('id', estimationId);
                      console.log('✅ Saved shorthand PM labor rate');
                    } catch (error) {
                      console.error('Error saving shorthand PM labor rate:', error);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                  }}
                />
              </div>
            </div>
            
            <div className="shorthand-input-card">
              <div className="shorthand-input-label">Estimated PM Hours</div>
              <div className="shorthand-input-wrapper">
                <input
                  type="text"
                  className="shorthand-input"
                  value={shorthandPMHoursInput}
                  onChange={(e) => setShorthandPMHoursInput(e.target.value)}
                  onBlur={async () => {
                    const parsed = parseInput(shorthandPMHoursInput) || 0;
                    const formatted = formatNumber(parsed);
                    setShorthandPMHours(parsed);
                    setShorthandPMHoursInput(formatted);
                    try {
                      await supabase
                        .from('estimations')
                        .update({ shorthand_pm_hours: parsed })
                        .eq('id', estimationId);
                      console.log('✅ Saved shorthand PM hours');
                    } catch (error) {
                      console.error('Error saving shorthand PM hours:', error);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                  }}
                />
              </div>
            </div>
          </div>
          
          <div className="shorthand-breakdown">
            <div className="shorthand-row">
              <span className="shorthand-label">Subtotal</span>
              <span className="shorthand-value">${formatCurrency(shorthand.subtotal)}</span>
            </div>
            <div className="shorthand-row">
              <span className="shorthand-label">
                Material Tax <span className="material-tax-badge">{materialTaxRate}%</span>
              </span>
              <span className="shorthand-value">${formatCurrency(shorthandMaterialCost * (materialTaxRate / 100))}</span>
            </div>
            <div className="shorthand-row">
              <span className="shorthand-label">
                Overhead <span className="overhead-badge">{overhead}%</span>
              </span>
              <span className="shorthand-value">${formatCurrency(shorthand.overheadAmount)}</span>
            </div>
            <div className="shorthand-row">
              <span className="shorthand-label">
                Net Profit <span className="profit-badge">{profit}%</span>
              </span>
              <span className="shorthand-value profit-value">${formatCurrency(shorthand.profitAmount)}</span>
            </div>
            <div className="shorthand-total-row">
              <span>Total</span>
              <span>${formatCurrency(shorthand.total)}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  if (laborSteps.length === 0) {
    return (
      <div className="estimate-container">
        <div className="estimate-header">
          <div className="estimate-title">
            <Calculator size={24} />
            <h2>Project Estimate</h2>
          </div>
          <div className="estimate-actions">
            <button onClick={loadLaborSteps} className="btn-action" title="Refresh">
              <RefreshCw size={18} />
            </button>
          </div>
        </div>

        {renderSettingsAndShorthand()}
      </div>
    );
  }

  return (
    <div 
      className="estimate-container" 
      style={{ position: 'relative', overflow: showAddItemModal ? 'hidden' : 'auto' }}
    >
      {/* Header */}
      <div className="estimate-header">
        <div className="estimate-title">
          <Calculator size={24} />
          <h2>Project Estimate</h2>
        </div>
        <div className="estimate-actions">
          <button onClick={loadLaborSteps} className="btn-action" title="Refresh">
            <RefreshCw size={18} />
          </button>
          <button onClick={exportToCSV} className="btn-action">
            <Download size={18} />
            Export CSV
          </button>
          <div style={{ position: 'relative' }} data-pdf-dropdown>
            <button 
              onClick={() => setShowPdfExportDropdown(!showPdfExportDropdown)} 
              className="btn-action"
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <FileDown size={18} />
              Export PDF
              <span style={{ fontSize: '10px', opacity: 0.7 }}>▼</span>
            </button>
            {showPdfExportDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '4px',
                background: 'var(--bg-primary)',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                border: '1px solid var(--border-color)',
                overflow: 'hidden',
                zIndex: 1000,
                minWidth: '200px'
              }}>
                <button
                  onClick={() => {
                    exportToPDF();
                    setShowPdfExportDropdown(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: 'var(--text-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                >
                  <FileDown size={16} />
                  Export as Material List
                </button>
                <button
                  onClick={() => {
                    setShowChangeOrderModal(true);
                    setShowPdfExportDropdown(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'none',
                    border: 'none',
                    borderTop: '1px solid var(--border-color)',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: 'var(--text-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                >
                  <FileDown size={16} />
                  Export as Change Order
          </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Settings Cards */}
      <div className="estimate-settings">
        <div className="settings-card-grid">
          <div className="setting-card blue-accent">
            <div className="setting-card-label">
              <Clock size={14} />
              LABOR RATE
            </div>
            <div className="setting-card-value-wrapper">
              <span className="input-prefix">$</span>
              <input
                type="text"
                className="setting-card-input"
                value={laborRateInput}
                onChange={(e) => setLaborRateInput(e.target.value)}
                onBlur={async () => {
                  const parsed = parseInput(laborRateInput) || 0;
                  const formatted = formatNumber(parsed);
                  setLaborRate(parsed);
                  setLaborRateInput(formatted);
                  try {
                    await supabase
                      .from('estimations')
                      .update({ labor_rate_per_hour: parsed })
                      .eq('id', estimationId);
                    console.log('✅ Saved labor rate to database');
                  } catch (error) {
                    console.error('Error saving labor rate:', error);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
              />
            </div>
          </div>
          
          <div className="setting-card orange-accent">
            <div className="setting-card-label">
              <Layers size={14} />
              OVERHEAD
            </div>
            <div className="setting-card-value-wrapper">
              <input
                type="text"
                className="setting-card-input"
                value={overheadInput}
                onChange={(e) => setOverheadInput(e.target.value)}
                onBlur={async () => {
                  const parsed = parseInput(overheadInput) || 0;
                  const formatted = formatNumber(parsed);
                  setOverhead(parsed);
                  setOverheadInput(formatted);
                  try {
                    await supabase
                      .from('estimations')
                      .update({ overhead_percentage: parsed })
                      .eq('id', estimationId);
                    console.log('✅ Saved overhead to database');
                  } catch (error) {
                    console.error('Error saving overhead:', error);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
              />
              <span className="input-suffix">%</span>
            </div>
          </div>
          
          <div className="setting-card green-accent">
            <div className="setting-card-label">
              <DollarSign size={14} />
              PROFIT MARGIN
            </div>
            <div className="setting-card-value-wrapper">
              <input
                type="text"
                className="setting-card-input"
                value={profitInput}
                onChange={(e) => setProfitInput(e.target.value)}
                onBlur={async () => {
                  const parsed = parseInput(profitInput) || 0;
                  const formatted = formatNumber(parsed);
                  setProfit(parsed);
                  setProfitInput(formatted);
                  try {
                    await supabase
                      .from('estimations')
                      .update({ profit_percentage: parsed })
                      .eq('id', estimationId);
                    console.log('✅ Saved profit to database');
                  } catch (error) {
                    console.error('Error saving profit:', error);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
              />
              <span className="input-suffix">%</span>
            </div>
          </div>
          
          <div className="setting-card purple-accent">
            <div className="setting-card-label">
              <Receipt size={14} />
              MATERIAL TAX
            </div>
            <div className="setting-card-value-wrapper">
              <input
                type="text"
                className="setting-card-input"
                value={materialTaxRateInput}
                onChange={(e) => setMaterialTaxRateInput(e.target.value)}
                onBlur={async () => {
                  const parsed = parseInput(materialTaxRateInput) || 0;
                  const formatted = formatNumber(parsed);
                  setMaterialTaxRate(parsed);
                  setMaterialTaxRateInput(formatted);
                  try {
                    await supabase
                      .from('estimations')
                      .update({ material_tax_rate: parsed })
                      .eq('id', estimationId);
                    console.log('✅ Saved material tax rate to database');
                  } catch (error) {
                    console.error('Error saving material tax rate:', error);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
              />
              <span className="input-suffix">%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Component Table */}
      <div className="estimate-table-wrapper">
        <table className="estimate-table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Qty</th>
              <th>Labor Units</th>
              <th>Labor Hours</th>
              <th style={{ textAlign: 'right' }}>Labor $</th>
              <th style={{ textAlign: 'right' }}>Material Price</th>
              <th style={{ textAlign: 'right' }}>Material $</th>
              <th style={{ textAlign: 'right' }}>Total</th>
              <th style={{ width: '50px' }}></th>
            </tr>
          </thead>
          <tbody>
            {laborSteps.map((step) => {
              const hours = calculateLaborHours(step);
              const key = `${step.matched_component}-${step.measurement}-${step.step_description || ''}`;
              const settings = getRowSettings(key);
              
              const currentQty = getEditedValue(step, 'quantity') || 0;
              const materialUnitCost = getEditedValue(step, 'materialCost') || 0;
              const totalMaterialCost = currentQty * materialUnitCost;
              
              const laborCost = hours * settings.laborRate;
              const subtotal = laborCost + totalMaterialCost;
              const overheadAmount = subtotal * (settings.overhead / 100);
              const profitAmount = (subtotal + overheadAmount) * (settings.profit / 100);
              const rowTotal = subtotal + overheadAmount + profitAmount;
              
              // Extract prefix like "Demo -" or "Install -" from step_description
              const descPrefix = step.step_description?.match(/^(Demo|Install)\s*-?\s*/i)?.[0]?.trim();
              const showPrefix = descPrefix && !step.matched_component.toLowerCase().startsWith(descPrefix.toLowerCase().replace('-', '').trim());
              
              return (
                <tr key={`${step.matched_component}-${step.measurement}-${step.step_description || ''}`}>
                  <td className="component-cell">
                    {showPrefix && (
                      <div className="component-prefix" style={{ 
                        fontSize: '10px', 
                        fontWeight: 600,
                        color: descPrefix?.toLowerCase().includes('demo') ? '#dc2626' : '#059669',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '2px'
                      }}>
                        {descPrefix.replace('-', '').trim()}
                      </div>
                    )}
                    <div className="component-name">{step.matched_component}</div>
                    <div className="component-size">{step.measurement}</div>
                  </td>
                  <td className="quantity-cell">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.125rem' }}>
                      {editingCell?.key === key && editingCell?.field === 'quantity' ? (
                      <input
                          type="text"
                        className="quantity-input-small"
                        value={cellInputValue}
                        onChange={(e) => setCellInputValue(e.target.value)}
                        onBlur={() => {
                          const parsed = parseInput(cellInputValue) || 0;
                          updateEditedValue(step, 'quantity', parsed);
                          setEditingCell(null);
                        }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                            if (e.key === 'Escape') setEditingCell(null);
                          }}
                          autoFocus
                        />
                      ) : (
                        <span
                        onClick={() => {
                          setEditingCell({ key, field: 'quantity' });
                          setCellInputValue(formatNumber(currentQty));
                        }}
                          style={{ cursor: 'pointer', padding: '4px 8px', minWidth: '60px' }}
                        >
                          {formatNumber(currentQty)}
                        </span>
                      )}
                      <span className="inline-unit">{step.quantity_unit}</span>
                    </div>
                  </td>
                  <td className="labor-units-cell" style={{ textAlign: 'center' }}>
                    <select
                      value={editedSteps.get(key)?.laborHours !== undefined ? 'custom' : (rowNecaConditions.get(key) || 'normal')}
                      onChange={async (e) => {
                        if (e.target.value === 'custom') {
                          // Switch to custom mode - restore saved custom value if exists
                          const savedCustomValue = savedCustomHours.get(key);
                          const valueToUse = savedCustomValue !== undefined ? savedCustomValue : hours;
                          
                          // Set custom hours in editedSteps (makes dropdown show "custom")
                          const updated = new Map(editedSteps);
                          const currentEdits = updated.get(key) || {};
                          currentEdits.laborHours = valueToUse;
                          updated.set(key, currentEdits);
                          setEditedSteps(updated);
                          
                          // Clear neca_condition from database so custom mode persists on reload
                          try {
                            await supabase
                              .from('estimate_labor_steps')
                              .update({ 
                                neca_condition: null,
                                labor_hours_override: valueToUse
                              })
                              .eq('estimation_id', estimationId)
                              .eq('matched_component', step.matched_component)
                              .eq('measurement', step.measurement)
                              .eq('step_description', step.step_description || '');
                            console.log(`✅ Switched to custom mode for ${step.matched_component}`);
                            
                            // Sync to component library
                            try {
                              const { data: estData } = await supabase
                                .from('estimations')
                                .select('user_id')
                                .eq('id', estimationId)
                                .single();
                              
                              if (estData?.user_id) {
                                const componentName = step.step_description || step.matched_component;
                                const { componentLibraryService } = await import('../services/componentLibraryService');
                                
                                await componentLibraryService.syncPricingData(estData.user_id, componentName, {
                                  material_cost: step.material_cost,
                                  labor_unit_type: step.unit as 'E' | 'C' | 'M',
                                  labor_units: {
                                    normal: step.normal_labor_units,
                                    difficult: step.difficult_labor_units,
                                    very_difficult: step.very_difficult_labor_units
                                  },
                                  preferred_labor_condition: undefined,  // Clear preferred condition when using custom
                                  custom_labor_hours: valueToUse
                                });
                                console.log(`💾 Synced custom mode to library for "${componentName}"`);
                              }
                            } catch (syncError) {
                              console.error('Failed to sync to library (non-critical):', syncError);
                            }
                          } catch (error) {
                            console.error('Error switching to custom mode:', error);
                          }
                          
                          // Open editor
                          setEditingCell({ key, field: 'laborHours' });
                          setCellInputValue(formatNumber(valueToUse));
                        } else {
                          const newCondition = e.target.value as 'normal' | 'difficult' | 'very_difficult';
                          
                          // If switching FROM custom, save the custom value for later
                          if (editedSteps.get(key)?.laborHours !== undefined) {
                            const customValue = editedSteps.get(key)!.laborHours!;
                            const updatedSavedCustom = new Map(savedCustomHours);
                            updatedSavedCustom.set(key, customValue);
                            setSavedCustomHours(updatedSavedCustom);
                            console.log(`💾 Saved custom hours ${customValue} for later restore`);
                            
                            // Clear from editedSteps so NECA values are used
                            const updated = new Map(editedSteps);
                            const currentEdits = updated.get(key) || {};
                            delete currentEdits.laborHours;
                            if (Object.keys(currentEdits).length === 0) {
                              updated.delete(key);
                            } else {
                              updated.set(key, currentEdits);
                            }
                            setEditedSteps(updated);
                          }
                          
                          // Update NECA condition
                          const updatedMap = new Map(rowNecaConditions);
                          updatedMap.set(key, newCondition);
                          setRowNecaConditions(updatedMap);
                          
                          // Save NECA condition to database
                          try {
                            await supabase
                              .from('estimate_labor_steps')
                              .update({ neca_condition: newCondition })
                              .eq('estimation_id', estimationId)
                              .eq('matched_component', step.matched_component)
                              .eq('measurement', step.measurement)
                              .eq('step_description', step.step_description || '');
                            console.log(`✅ Saved NECA condition for ${step.matched_component}`);
                            
                            // Sync to component library (preserve custom hours in saved state)
                            try {
                              const { data: estData } = await supabase
                                .from('estimations')
                                .select('user_id')
                                .eq('id', estimationId)
                                .single();
                              
                              if (estData?.user_id) {
                                const componentName = step.step_description || step.matched_component;
                                const { componentLibraryService } = await import('../services/componentLibraryService');
                                const customHours = savedCustomHours.get(key);
                              
                                await componentLibraryService.syncPricingData(estData.user_id, componentName, {
                                  material_cost: step.material_cost,
                                  labor_unit_type: step.unit as 'E' | 'C' | 'M',
                                  labor_units: {
                                    normal: step.normal_labor_units,
                                    difficult: step.difficult_labor_units,
                                    very_difficult: step.very_difficult_labor_units
                                  },
                                  preferred_labor_condition: newCondition,
                                  custom_labor_hours: customHours  // Preserve saved custom hours
                                });
                                console.log(`💾 Synced NECA condition to library for "${componentName}"`);
                              }
                            } catch (syncError) {
                              console.error('Failed to sync to library (non-critical):', syncError);
                            }
                          } catch (error) {
                            console.error('Error saving row NECA condition:', error);
                          }
                        }
                      }}
                      style={{
                        padding: '6px 8px',
                        fontSize: '13px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        background: 'var(--bg-primary)',
                        cursor: 'pointer',
                        minWidth: '100px'
                      }}
                    >
                      <option value="normal">NECA 1</option>
                      <option value="difficult">NECA 2</option>
                      <option value="very_difficult">NECA 3</option>
                      <option value="custom">Custom</option>
                    </select>
                    <div 
                      style={{ 
                        fontSize: '13px', 
                        color: 'var(--text-secondary)', 
                        marginTop: '4px',
                        padding: '4px 8px',
                        fontWeight: 600
                      }}
                    >
                      {(() => {
                        const rowCondition = rowNecaConditions.get(key) || 'normal';
                        let laborUnit = 0;
                        if (rowCondition === 'normal') {
                          laborUnit = step.normal_labor_units;
                        } else if (rowCondition === 'difficult') {
                          laborUnit = step.difficult_labor_units;
                        } else {
                          laborUnit = step.very_difficult_labor_units;
                        }
                        return editedSteps.get(key)?.laborHours !== undefined 
                          ? <span style={{ color: '#3b82f6' }}>Custom</span>
                          : <span>{formatNumber(laborUnit)} {step.unit}</span>;
                      })()}
                    </div>
                  </td>
                  <td className="labor-hours-cell" style={{ textAlign: 'center' }}>
                    <div 
                      style={{ 
                        fontSize: '12px', 
                        color: editedSteps.get(key)?.laborHours !== undefined ? '#3b82f6' : 'var(--text-muted)', 
                        cursor: 'pointer',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        transition: 'all 0.2s',
                        fontWeight: editedSteps.get(key)?.laborHours !== undefined ? 600 : 400,
                        border: '1px solid transparent'
                      }}
                      onClick={() => {
                        const savedCustom = savedCustomHours.get(key);
                        const currentCustom = editedSteps.get(key)?.laborHours;
                        const valueToEdit = currentCustom !== undefined ? currentCustom : (savedCustom !== undefined ? savedCustom : hours);
                        
                        setEditingCell({ key, field: 'laborHours' });
                        setCellInputValue(formatNumber(valueToEdit));
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--bg-tertiary)';
                        e.currentTarget.style.borderColor = 'var(--border-strong)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.borderColor = 'transparent';
                      }}
                    >
                      {editingCell?.key === key && editingCell?.field === 'laborHours' ? (
                        <input
                          type="text"
                          className="row-setting-input"
                          value={cellInputValue}
                          onChange={(e) => setCellInputValue(e.target.value)}
                          onBlur={async () => {
                            const parsed = parseInput(cellInputValue) || 0;
                            updateEditedValue(step, 'laborHours', parsed);
                            setEditingCell(null);
                            
                            // Save to savedCustomHours for restoration
                            const updatedSavedCustom = new Map(savedCustomHours);
                            updatedSavedCustom.set(key, parsed);
                            setSavedCustomHours(updatedSavedCustom);
                            
                            // Sync custom hours to library
                            try {
                              const { data: estData } = await supabase
                                .from('estimations')
                                .select('user_id')
                                .eq('id', estimationId)
                                .single();
                              
                              if (estData?.user_id) {
                                const componentName = step.step_description || step.matched_component;
                                const { componentLibraryService } = await import('../services/componentLibraryService');
                                
                                await componentLibraryService.syncPricingData(estData.user_id, componentName, {
                                  material_cost: step.material_cost,
                                  labor_unit_type: step.unit as 'E' | 'C' | 'M',
                                  labor_units: {
                                    normal: step.normal_labor_units,
                                    difficult: step.difficult_labor_units,
                                    very_difficult: step.very_difficult_labor_units
                                  },
                                  preferred_labor_condition: rowNecaConditions.get(key) || 'normal',
                                  custom_labor_hours: parsed
                                });
                                console.log(`💾 Synced custom labor hours to library for "${componentName}"`);
                              }
                            } catch (syncError) {
                              console.error('Failed to sync custom hours to library (non-critical):', syncError);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                            if (e.key === 'Escape') {
                              setEditingCell(null);
                              setCellInputValue('');
                            }
                          }}
                          autoFocus
                          style={{ 
                            width: '70px',
                            textAlign: 'center',
                            padding: '2px 4px',
                            border: '1px solid #3b82f6',
                            borderRadius: '3px',
                            fontSize: '11px',
                            background: 'var(--bg-primary)'
                          }}
                        />
                      ) : (
                        <span>{formatNumber(hours)} hrs</span>
                      )}
                    </div>
                  </td>
                  <td className="cost-cell" style={{ textAlign: 'right' }}>${formatCurrency(laborCost)}</td>
                  <td className="material-cell" style={{ textAlign: 'right', paddingRight: '12px' }}>
                    {editingCell?.key === key && editingCell?.field === 'materialCost' ? (
                      <input
                        type="text"
                        className="row-setting-input"
                        value={cellInputValue}
                        onChange={(e) => setCellInputValue(e.target.value)}
                        onBlur={() => {
                          const parsed = parseInput(cellInputValue) || 0;
                          updateEditedValue(step, 'materialCost', parsed);
                          setEditingCell(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.currentTarget.blur();
                          if (e.key === 'Escape') setEditingCell(null);
                        }}
                        autoFocus
                        style={{ 
                          width: '100%',
                          textAlign: 'right',
                          padding: '4px 8px',
                          border: '2px solid #3b82f6',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                    ) : (
                      <span
                        onClick={() => {
                          setEditingCell({ key, field: 'materialCost' });
                          setCellInputValue(formatNumber(materialUnitCost));
                        }}
                        title={step.material_product_title ? `${step.material_product_title}\n\nClick to edit` : 'Click to edit material price'}
                        style={{
                          cursor: 'pointer',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          display: 'inline-block',
                          transition: 'background 0.2s',
                          minWidth: '60px',
                          textAlign: 'right'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--bg-tertiary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        ${materialUnitCost.toFixed(2)}
                      </span>
                    )}
                  </td>
                  <td className="cost-cell" style={{ textAlign: 'right' }}>${formatCurrency(totalMaterialCost)}</td>
                  <td className="total-cell" style={{ textAlign: 'right' }}>${formatCurrency(rowTotal)}</td>
                  <td style={{ textAlign: 'center', padding: '0.5rem' }}>
                    <button
                      onClick={() => deleteRow(step)}
                      className="btn-delete-row"
                      title="Delete row"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#ef4444',
                        cursor: 'pointer',
                        padding: '0.25rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--red-tint-medium)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              );
            })}
            
            <tr className="totals-summary-row">
              <td colSpan={2} className="totals-cell"><strong>TOTALS</strong></td>
              <td></td>
              <td className="totals-cell" style={{ textAlign: 'center' }}><strong>{formatNumber(totals.laborHours)} hrs</strong></td>
              <td className="totals-cell" style={{ textAlign: 'right' }}><strong>${formatCurrency(totals.laborCost)}</strong></td>
              <td></td>
              <td className="totals-cell" style={{ textAlign: 'right' }}><strong>${formatCurrency(totals.materialCost)}</strong></td>
              <td className="totals-cell" style={{ textAlign: 'right' }}><strong>${formatCurrency(totals.total + (totals.materialCost * (materialTaxRate / 100)))}</strong></td>
              <td></td>
            </tr>
          </tbody>
        </table>
        
        {/* Add New Item Button */}
        <button
          onClick={openAddItemModal}
          style={{
            width: '100%',
            padding: '1rem',
            marginTop: '1rem',
            background: 'linear-gradient(135deg, var(--blue-tint-light) 0%, var(--blue-tint-medium) 100%)',
            border: '2px dashed #3b82f6',
            borderRadius: '8px',
            color: '#3b82f6',
            fontSize: '0.9375rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, var(--blue-tint-medium) 0%, var(--blue-tint-strong) 100%)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, var(--blue-tint-light) 0%, var(--blue-tint-medium) 100%)';
          }}
        >
          + Add New Item
        </button>
      </div>
      
      {/* Ground Management Table - Right below main sheet */}
      <div className="estimate-table-wrapper" style={{ marginTop: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
            Ground Management
          </h3>
          <button
            onClick={() => {
              setNewGroundManagementRate(formatNumber(laborRate));
              setShowAddGroundManagementModal(true);
            }}
            style={{
              padding: '0.5rem 1rem',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              border: 'none',
              borderRadius: '6px',
              color: '#ffffff',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem'
            }}
          >
            <Plus size={16} />
            Add Role
          </button>
        </div>
        
        {groundManagementItems.length > 0 ? (
          <table className="estimate-table">
            <thead>
              <tr>
                <th>Role</th>
                <th style={{ textAlign: 'center' }}>Hours</th>
                <th style={{ textAlign: 'right' }}>Rate</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ width: '50px' }}></th>
              </tr>
            </thead>
            <tbody>
              {groundManagementItems.map((item) => (
                <tr key={item.id}>
                  <td className="component-cell"><div className="component-name">{item.role}</div></td>
                  <td style={{ textAlign: 'center' }}>{formatNumber(item.hours)} hrs</td>
                  <td style={{ textAlign: 'right' }}>${formatCurrency(item.hourly_rate)}/hr</td>
                  <td className="cost-cell" style={{ textAlign: 'right' }}>${formatCurrency(item.hours * item.hourly_rate)}</td>
                  <td style={{ textAlign: 'center', padding: '0.5rem' }}>
                    <button onClick={() => deleteGroundManagementItem(item.id)} className="btn-delete-row" title="Delete" style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.25rem' }}>
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="totals-summary-row">
                <td className="totals-cell"><strong>GROUND MANAGEMENT TOTAL</strong></td>
                <td></td><td></td>
                <td className="totals-cell" style={{ textAlign: 'right' }}><strong>${formatCurrency(totals.groundManagementTotal)}</strong></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        ) : (
          <div style={{ padding: '2rem', textAlign: 'center', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px dashed var(--border-strong)', color: 'var(--text-muted)' }}>
            <p style={{ margin: 0, fontSize: '0.9375rem' }}>No ground management entries yet.</p>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem' }}>Add Foreman, General Foreman, Superintendent, etc.</p>
          </div>
        )}
      </div>

      {/* Add Ground Management Modal */}
      {showAddGroundManagementModal && createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={(e) => { if (e.target === e.currentTarget) setShowAddGroundManagementModal(false); }}>
          <div style={{ background: 'var(--bg-primary)', borderRadius: '12px', width: '90%', maxWidth: '450px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '700', color: 'var(--text-primary)' }}>Add Ground Management</h3>
              <button onClick={() => setShowAddGroundManagementModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.25rem', color: 'var(--text-muted)' }}>✕</button>
            </div>
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>Role</label>
                <select value={newGroundManagementRole} onChange={(e) => setNewGroundManagementRole(e.target.value)} style={{ width: '100%', padding: '0.75rem 1rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', fontSize: '0.9375rem', boxSizing: 'border-box', cursor: 'pointer' }}>
                  <option value="Foreman">Foreman</option>
                  <option value="General Foreman">General Foreman</option>
                  <option value="Superintendent">Superintendent</option>
                  <option value="Safety Manager">Safety Manager</option>
                  <option value="QC Manager">QC Manager</option>
                  <option value="Project Engineer">Project Engineer</option>
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>Hours</label>
                  <input type="text" value={newGroundManagementHours} onChange={(e) => setNewGroundManagementHours(e.target.value)} placeholder="0.00" style={{ width: '100%', padding: '0.75rem 1rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', fontSize: '0.9375rem', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>Hourly Rate</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}>$</span>
                    <input type="text" value={newGroundManagementRate} onChange={(e) => setNewGroundManagementRate(e.target.value)} placeholder={formatNumber(laborRate)} style={{ width: '100%', padding: '0.75rem 1rem 0.75rem 2rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', fontSize: '0.9375rem', boxSizing: 'border-box' }} />
                  </div>
                </div>
              </div>
              {/* Preview */}
              {(newGroundManagementHours && newGroundManagementRate) && (
                <div style={{ padding: '0.75rem 1rem', background: 'var(--green-tint-light)', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: 'var(--green-text)', fontSize: '0.875rem', fontWeight: '500' }}>Total:</span>
                  <span style={{ color: 'var(--green-text)', fontSize: '1rem', fontWeight: '700' }}>${formatCurrency((parseInput(newGroundManagementHours) || 0) * (parseInput(newGroundManagementRate) || 0))}</span>
                </div>
              )}
            </div>
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', background: 'var(--bg-secondary)' }}>
              <button onClick={() => setShowAddGroundManagementModal(false)} style={{ padding: '0.75rem 1.5rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-muted)', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
              <button onClick={addGroundManagementItem} style={{ padding: '0.75rem 1.5rem', border: 'none', borderRadius: '8px', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: '#ffffff', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>Add Role</button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Miscellaneous Items Table */}
      {miscItems.length > 0 && (
        <div className="estimate-table-wrapper" style={{ marginTop: '2rem' }}>
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: '700',
            color: 'var(--text-primary)',
            marginBottom: '1rem'
          }}>
            Miscellaneous Items
          </h3>
          <table className="estimate-table">
            <thead>
              <tr>
                <th>Item Name</th>
                <th style={{ textAlign: 'right' }}>Price</th>
                <th style={{ width: '50px' }}></th>
              </tr>
            </thead>
            <tbody>
              {miscItems.map((item) => (
                <tr key={item.id}>
                  <td className="component-cell">
                    <div className="component-name">{item.item_name}</div>
                    {item.description && <div className="component-size">{item.description}</div>}
                  </td>
                  <td className="cost-cell" style={{ textAlign: 'right' }}>${formatCurrency(item.price)}</td>
                  <td style={{ textAlign: 'center', padding: '0.5rem' }}>
                    <button
                      onClick={async () => {
                        if (window.confirm('Delete this miscellaneous item?')) {
                          try {
                            await supabase
                              .from('estimate_miscellaneous')
                              .delete()
                              .eq('id', item.id);
                            await loadMiscItems();
                          } catch (error) {
                            console.error('Error deleting misc item:', error);
                          }
                        }
                      }}
                      className="btn-delete-row"
                      title="Delete item"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#ef4444',
                        cursor: 'pointer',
                        padding: '0.25rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--red-tint-medium)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="totals-summary-row">
                <td className="totals-cell"><strong>MISC TOTAL</strong></td>
                <td className="totals-cell" style={{ textAlign: 'right' }}><strong>${formatCurrency(totals.miscTotal)}</strong></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Incidental Labor Table */}
      <div className="estimate-table-wrapper" style={{ marginTop: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: '700',
            color: 'var(--text-primary)',
            margin: 0
          }}>
            Incidental Labor
          </h3>
          <button
            onClick={() => {
              setNewIncidentalRate(formatNumber(laborRate));
              setShowAddIncidentalLaborModal(true);
            }}
            style={{
              padding: '0.5rem 1rem',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              border: 'none',
              borderRadius: '6px',
              color: '#ffffff',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)'
            }}
          >
            <Plus size={16} />
            Add Labor
          </button>
        </div>
        
        {incidentalLaborItems.length > 0 ? (
          <table className="estimate-table">
            <thead>
              <tr>
                <th>Description</th>
                <th style={{ textAlign: 'center' }}>Hours</th>
                <th style={{ textAlign: 'right' }}>Rate</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ width: '50px' }}></th>
              </tr>
            </thead>
            <tbody>
              {incidentalLaborItems.map((item) => (
                <tr key={item.id}>
                  <td className="component-cell">
                    <div className="component-name">{item.description}</div>
                  </td>
                  <td style={{ textAlign: 'center' }}>{formatNumber(item.hours)} hrs</td>
                  <td style={{ textAlign: 'right' }}>${formatCurrency(item.hourly_rate)}/hr</td>
                  <td className="cost-cell" style={{ textAlign: 'right' }}>${formatCurrency(item.hours * item.hourly_rate)}</td>
                  <td style={{ textAlign: 'center', padding: '0.5rem' }}>
                    <button
                      onClick={() => deleteIncidentalLaborItem(item.id)}
                      className="btn-delete-row"
                      title="Delete item"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#ef4444',
                        cursor: 'pointer',
                        padding: '0.25rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--red-tint-medium)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="totals-summary-row">
                <td className="totals-cell"><strong>INCIDENTAL LABOR TOTAL</strong></td>
                <td></td>
                <td></td>
                <td className="totals-cell" style={{ textAlign: 'right' }}><strong>${formatCurrency(totals.incidentalLaborTotal)}</strong></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        ) : (
          <div style={{
            padding: '2rem',
            textAlign: 'center',
            background: 'var(--bg-secondary)',
            borderRadius: '8px',
            border: '1px dashed var(--border-strong)',
            color: 'var(--text-muted)'
          }}>
            <p style={{ margin: 0, fontSize: '0.9375rem' }}>No incidental labor items yet.</p>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem' }}>Click "Add Labor" to add manual labor entries.</p>
          </div>
        )}
      </div>

      {/* Add Incidental Labor Modal */}
      {showAddIncidentalLaborModal && createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAddIncidentalLaborModal(false);
            }
          }}
        >
          <div style={{
            background: 'var(--bg-primary)',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '450px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '1.25rem 1.5rem',
              borderBottom: '1px solid var(--border-color)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.125rem',
                fontWeight: '700',
                color: 'var(--text-primary)'
              }}>
                Add Incidental Labor
              </h3>
              <button
                onClick={() => setShowAddIncidentalLaborModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  color: 'var(--text-muted)'
                }}
              >
                ✕
              </button>
            </div>
            
            {/* Modal Body */}
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: 'var(--text-secondary)'
                }}>Description</label>
                <input
                  type="text"
                  value={newIncidentalDescription}
                  onChange={(e) => setNewIncidentalDescription(e.target.value)}
                  placeholder="e.g., Site cleanup, Material handling"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1.5px solid var(--border-color)',
                    borderRadius: '8px',
                    fontSize: '0.9375rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: 'var(--text-secondary)'
                  }}>Hours</label>
                  <input
                    type="text"
                    value={newIncidentalHours}
                    onChange={(e) => setNewIncidentalHours(e.target.value)}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: '1.5px solid var(--border-color)',
                      borderRadius: '8px',
                      fontSize: '0.9375rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: 'var(--text-secondary)'
                  }}>Hourly Rate</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{
                      position: 'absolute',
                      left: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--text-muted)',
                      fontSize: '0.9375rem',
                      fontWeight: '500'
                    }}>$</span>
                    <input
                      type="text"
                      value={newIncidentalRate}
                      onChange={(e) => setNewIncidentalRate(e.target.value)}
                      placeholder={formatNumber(laborRate)}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem 0.75rem 2rem',
                        border: '1.5px solid var(--border-color)',
                        borderRadius: '8px',
                        fontSize: '0.9375rem',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </div>
              
              {/* Preview */}
              {(newIncidentalHours && newIncidentalRate) && (
                <div style={{
                  padding: '0.75rem 1rem',
                  background: 'var(--green-tint-light)',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ color: 'var(--green-text)', fontSize: '0.875rem', fontWeight: '500' }}>Total:</span>
                  <span style={{ color: 'var(--green-text)', fontSize: '1rem', fontWeight: '700' }}>
                    ${formatCurrency((parseInput(newIncidentalHours) || 0) * (parseInput(newIncidentalRate) || 0))}
                  </span>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div style={{
              padding: '1rem 1.5rem',
              borderTop: '1px solid var(--border-color)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.75rem',
              background: 'var(--bg-secondary)'
            }}>
              <button
                onClick={() => setShowAddIncidentalLaborModal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '1.5px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-muted)',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={addIncidentalLaborItem}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: '#ffffff',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                }}
              >
                Add Labor
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Indirect Labor Table */}
      <div className="estimate-table-wrapper" style={{ marginTop: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
            Indirect Labor
          </h3>
          <button
            onClick={() => {
              setNewIndirectRate(formatNumber(laborRate));
              setShowAddIndirectLaborModal(true);
            }}
            style={{
              padding: '0.5rem 1rem',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              border: 'none',
              borderRadius: '6px',
              color: '#ffffff',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem'
            }}
          >
            <Plus size={16} />
            Add Indirect
          </button>
        </div>
        
        {indirectLaborItems.length > 0 ? (
          <table className="estimate-table">
            <thead>
              <tr>
                <th>Description</th>
                <th style={{ textAlign: 'center' }}>Hours</th>
                <th style={{ textAlign: 'right' }}>Rate</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ width: '50px' }}></th>
              </tr>
            </thead>
            <tbody>
              {indirectLaborItems.map((item) => (
                <tr key={item.id}>
                  <td className="component-cell"><div className="component-name">{item.description}</div></td>
                  <td style={{ textAlign: 'center' }}>{formatNumber(item.hours)} hrs</td>
                  <td style={{ textAlign: 'right' }}>${formatCurrency(item.hourly_rate)}/hr</td>
                  <td className="cost-cell" style={{ textAlign: 'right' }}>${formatCurrency(item.hours * item.hourly_rate)}</td>
                  <td style={{ textAlign: 'center', padding: '0.5rem' }}>
                    <button onClick={() => deleteIndirectLaborItem(item.id)} className="btn-delete-row" title="Delete" style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.25rem' }}>
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="totals-summary-row">
                <td className="totals-cell"><strong>INDIRECT LABOR TOTAL</strong></td>
                <td></td><td></td>
                <td className="totals-cell" style={{ textAlign: 'right' }}><strong>${formatCurrency(totals.indirectLaborTotal)}</strong></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        ) : (
          <div style={{ padding: '2rem', textAlign: 'center', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px dashed var(--border-strong)', color: 'var(--text-muted)' }}>
            <p style={{ margin: 0, fontSize: '0.9375rem' }}>No indirect labor items yet.</p>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem' }}>Click "Add Indirect" to add entries.</p>
          </div>
        )}
      </div>

      {/* Add Indirect Labor Modal */}
      {showAddIndirectLaborModal && createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={(e) => { if (e.target === e.currentTarget) setShowAddIndirectLaborModal(false); }}>
          <div style={{ background: 'var(--bg-primary)', borderRadius: '12px', width: '90%', maxWidth: '450px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '700', color: 'var(--text-primary)' }}>Add Indirect Labor</h3>
              <button onClick={() => setShowAddIndirectLaborModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.25rem', color: 'var(--text-muted)' }}>✕</button>
            </div>
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>Description</label>
                <input type="text" value={newIndirectDescription} onChange={(e) => setNewIndirectDescription(e.target.value)} placeholder="e.g., Supervision, Safety meetings" style={{ width: '100%', padding: '0.75rem 1rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', fontSize: '0.9375rem', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>Hours</label>
                  <input type="text" value={newIndirectHours} onChange={(e) => setNewIndirectHours(e.target.value)} placeholder="0.00" style={{ width: '100%', padding: '0.75rem 1rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', fontSize: '0.9375rem', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>Hourly Rate</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}>$</span>
                    <input type="text" value={newIndirectRate} onChange={(e) => setNewIndirectRate(e.target.value)} placeholder={formatNumber(laborRate)} style={{ width: '100%', padding: '0.75rem 1rem 0.75rem 2rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', fontSize: '0.9375rem', boxSizing: 'border-box' }} />
                  </div>
                </div>
              </div>
            </div>
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', background: 'var(--bg-secondary)' }}>
              <button onClick={() => setShowAddIndirectLaborModal(false)} style={{ padding: '0.75rem 1.5rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-muted)', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
              <button onClick={addIndirectLaborItem} style={{ padding: '0.75rem 1.5rem', border: 'none', borderRadius: '8px', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: '#ffffff', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>Add Labor</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Subcontractors Table */}
      <div className="estimate-table-wrapper" style={{ marginTop: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
            Subcontractors
          </h3>
          <button
            onClick={() => setShowAddSubcontractorModal(true)}
            style={{
              padding: '0.5rem 1rem',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              border: 'none',
              borderRadius: '6px',
              color: '#ffffff',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem'
            }}
          >
            <Plus size={16} />
            Add Subcontractor
          </button>
        </div>
        
        {subcontractorItems.length > 0 ? (
          <table className="estimate-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th style={{ textAlign: 'right' }}>Price</th>
                <th style={{ width: '50px' }}></th>
              </tr>
            </thead>
            <tbody>
              {subcontractorItems.map((item) => (
                <tr key={item.id}>
                  <td className="component-cell"><div className="component-name">{item.name}</div></td>
                  <td>{item.description || '-'}</td>
                  <td className="cost-cell" style={{ textAlign: 'right' }}>${formatCurrency(item.price)}</td>
                  <td style={{ textAlign: 'center', padding: '0.5rem' }}>
                    <button onClick={() => deleteSubcontractorItem(item.id)} className="btn-delete-row" title="Delete" style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.25rem' }}>
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="totals-summary-row">
                <td className="totals-cell"><strong>SUBCONTRACTOR TOTAL</strong></td>
                <td></td>
                <td className="totals-cell" style={{ textAlign: 'right' }}><strong>${formatCurrency(totals.subcontractorTotal)}</strong></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        ) : (
          <div style={{ padding: '2rem', textAlign: 'center', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px dashed var(--border-strong)', color: 'var(--text-muted)' }}>
            <p style={{ margin: 0, fontSize: '0.9375rem' }}>No subcontractors yet.</p>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem' }}>Click "Add Subcontractor" to add entries.</p>
          </div>
        )}
      </div>

      {/* Add Subcontractor Modal */}
      {showAddSubcontractorModal && createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={(e) => { if (e.target === e.currentTarget) setShowAddSubcontractorModal(false); }}>
          <div style={{ background: 'var(--bg-primary)', borderRadius: '12px', width: '90%', maxWidth: '450px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '700', color: 'var(--text-primary)' }}>Add Subcontractor</h3>
              <button onClick={() => setShowAddSubcontractorModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.25rem', color: 'var(--text-muted)' }}>✕</button>
            </div>
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>Subcontractor Name</label>
                <input type="text" value={newSubcontractorName} onChange={(e) => setNewSubcontractorName(e.target.value)} placeholder="e.g., ABC Plumbing" style={{ width: '100%', padding: '0.75rem 1rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', fontSize: '0.9375rem', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>Description (Optional)</label>
                <input type="text" value={newSubcontractorDescription} onChange={(e) => setNewSubcontractorDescription(e.target.value)} placeholder="Scope of work..." style={{ width: '100%', padding: '0.75rem 1rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', fontSize: '0.9375rem', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>Total Price</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}>$</span>
                  <input type="text" value={newSubcontractorPrice} onChange={(e) => setNewSubcontractorPrice(e.target.value)} placeholder="0.00" style={{ width: '100%', padding: '0.75rem 1rem 0.75rem 2rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', fontSize: '0.9375rem', boxSizing: 'border-box' }} />
                </div>
              </div>
            </div>
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', background: 'var(--bg-secondary)' }}>
              <button onClick={() => setShowAddSubcontractorModal(false)} style={{ padding: '0.75rem 1.5rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-muted)', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
              <button onClick={addSubcontractorItem} style={{ padding: '0.75rem 1.5rem', border: 'none', borderRadius: '8px', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: '#ffffff', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>Add Subcontractor</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Equipment Table */}
      <div className="estimate-table-wrapper" style={{ marginTop: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
            Equipment
          </h3>
          <button
            onClick={() => setShowAddEquipmentModal(true)}
            style={{
              padding: '0.5rem 1rem',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              border: 'none',
              borderRadius: '6px',
              color: '#ffffff',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem'
            }}
          >
            <Plus size={16} />
            Add Equipment
          </button>
        </div>
        
        {equipmentItems.length > 0 ? (
          <table className="estimate-table">
            <thead>
              <tr>
                <th>Equipment</th>
                <th>Description</th>
                <th style={{ textAlign: 'right' }}>Price</th>
                <th style={{ width: '50px' }}></th>
              </tr>
            </thead>
            <tbody>
              {equipmentItems.map((item) => (
                <tr key={item.id}>
                  <td className="component-cell"><div className="component-name">{item.name}</div></td>
                  <td>{item.description || '-'}</td>
                  <td className="cost-cell" style={{ textAlign: 'right' }}>${formatCurrency(item.price)}</td>
                  <td style={{ textAlign: 'center', padding: '0.5rem' }}>
                    <button onClick={() => deleteEquipmentItem(item.id)} className="btn-delete-row" title="Delete" style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.25rem' }}>
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="totals-summary-row">
                <td className="totals-cell"><strong>EQUIPMENT TOTAL</strong></td>
                <td></td>
                <td className="totals-cell" style={{ textAlign: 'right' }}><strong>${formatCurrency(totals.equipmentTotal)}</strong></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        ) : (
          <div style={{ padding: '2rem', textAlign: 'center', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px dashed var(--border-strong)', color: 'var(--text-muted)' }}>
            <p style={{ margin: 0, fontSize: '0.9375rem' }}>No equipment items yet.</p>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem' }}>Click "Add Equipment" to add entries.</p>
          </div>
        )}
      </div>

      {/* Add Equipment Modal */}
      {showAddEquipmentModal && createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={(e) => { if (e.target === e.currentTarget) setShowAddEquipmentModal(false); }}>
          <div style={{ background: 'var(--bg-primary)', borderRadius: '12px', width: '90%', maxWidth: '450px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '700', color: 'var(--text-primary)' }}>Add Equipment</h3>
              <button onClick={() => setShowAddEquipmentModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.25rem', color: 'var(--text-muted)' }}>✕</button>
            </div>
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>Equipment Name</label>
                <input type="text" value={newEquipmentName} onChange={(e) => setNewEquipmentName(e.target.value)} placeholder="e.g., Scissor lift rental" style={{ width: '100%', padding: '0.75rem 1rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', fontSize: '0.9375rem', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>Description (Optional)</label>
                <input type="text" value={newEquipmentDescription} onChange={(e) => setNewEquipmentDescription(e.target.value)} placeholder="Duration, specs..." style={{ width: '100%', padding: '0.75rem 1rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', fontSize: '0.9375rem', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>Total Price</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}>$</span>
                  <input type="text" value={newEquipmentPrice} onChange={(e) => setNewEquipmentPrice(e.target.value)} placeholder="0.00" style={{ width: '100%', padding: '0.75rem 1rem 0.75rem 2rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', fontSize: '0.9375rem', boxSizing: 'border-box' }} />
                </div>
              </div>
            </div>
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', background: 'var(--bg-secondary)' }}>
              <button onClick={() => setShowAddEquipmentModal(false)} style={{ padding: '0.75rem 1.5rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-muted)', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
              <button onClick={addEquipmentItem} style={{ padding: '0.75rem 1.5rem', border: 'none', borderRadius: '8px', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: '#ffffff', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>Add Equipment</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Quoted Materials Table */}
      <div className="estimate-table-wrapper" style={{ marginTop: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
            Quoted Materials
          </h3>
          <button
            onClick={() => setShowAddQuotedMaterialsModal(true)}
            style={{
              padding: '0.5rem 1rem',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              border: 'none',
              borderRadius: '6px',
              color: '#ffffff',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem'
            }}
          >
            <Plus size={16} />
            Add Material
          </button>
        </div>
        
        {quotedMaterialsItems.length > 0 ? (
          <table className="estimate-table">
            <thead>
              <tr>
                <th>Material</th>
                <th style={{ textAlign: 'center' }}>Qty</th>
                <th style={{ textAlign: 'center' }}>Unit</th>
                <th style={{ textAlign: 'right' }}>Unit Price</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ width: '50px' }}></th>
              </tr>
            </thead>
            <tbody>
              {quotedMaterialsItems.map((item) => (
                <tr key={item.id}>
                  <td className="component-cell">
                    <div className="component-name">{item.name}</div>
                    {item.description && <div className="component-size">{item.description}</div>}
                  </td>
                  <td style={{ textAlign: 'center' }}>{formatNumber(item.quantity)}</td>
                  <td style={{ textAlign: 'center' }}>{item.unit}</td>
                  <td style={{ textAlign: 'right' }}>${formatCurrency(item.unit_price)}</td>
                  <td className="cost-cell" style={{ textAlign: 'right' }}>${formatCurrency(item.quantity * item.unit_price)}</td>
                  <td style={{ textAlign: 'center', padding: '0.5rem' }}>
                    <button onClick={() => deleteQuotedMaterialsItem(item.id)} className="btn-delete-row" title="Delete" style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.25rem' }}>
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="totals-summary-row">
                <td className="totals-cell"><strong>QUOTED MATERIALS TOTAL</strong></td>
                <td></td><td></td><td></td>
                <td className="totals-cell" style={{ textAlign: 'right' }}><strong>${formatCurrency(totals.quotedMaterialsTotal)}</strong></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        ) : (
          <div style={{ padding: '2rem', textAlign: 'center', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px dashed var(--border-strong)', color: 'var(--text-muted)' }}>
            <p style={{ margin: 0, fontSize: '0.9375rem' }}>No quoted materials yet.</p>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem' }}>Click "Add Material" to add entries.</p>
          </div>
        )}
      </div>

      {/* Add Quoted Materials Modal */}
      {showAddQuotedMaterialsModal && createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={(e) => { if (e.target === e.currentTarget) setShowAddQuotedMaterialsModal(false); }}>
          <div style={{ background: 'var(--bg-primary)', borderRadius: '12px', width: '90%', maxWidth: '500px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '700', color: 'var(--text-primary)' }}>Add Quoted Material</h3>
              <button onClick={() => setShowAddQuotedMaterialsModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.25rem', color: 'var(--text-muted)' }}>✕</button>
            </div>
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>Material Name</label>
                <input type="text" value={newQuotedMaterialName} onChange={(e) => setNewQuotedMaterialName(e.target.value)} placeholder="e.g., Switchgear" style={{ width: '100%', padding: '0.75rem 1rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', fontSize: '0.9375rem', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>Description (Optional)</label>
                <input type="text" value={newQuotedMaterialDescription} onChange={(e) => setNewQuotedMaterialDescription(e.target.value)} placeholder="Model, specs..." style={{ width: '100%', padding: '0.75rem 1rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', fontSize: '0.9375rem', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>Quantity</label>
                  <input type="text" value={newQuotedMaterialQty} onChange={(e) => setNewQuotedMaterialQty(e.target.value)} placeholder="1" style={{ width: '100%', padding: '0.75rem 1rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', fontSize: '0.9375rem', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>Unit</label>
                  <select value={newQuotedMaterialUnit} onChange={(e) => setNewQuotedMaterialUnit(e.target.value)} style={{ width: '100%', padding: '0.75rem 1rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', fontSize: '0.9375rem', boxSizing: 'border-box', cursor: 'pointer' }}>
                    <option value="EA">EA</option>
                    <option value="FT">FT</option>
                    <option value="LF">LF</option>
                    <option value="LOT">LOT</option>
                    <option value="SET">SET</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>Unit Price</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}>$</span>
                    <input type="text" value={newQuotedMaterialPrice} onChange={(e) => setNewQuotedMaterialPrice(e.target.value)} placeholder="0.00" style={{ width: '100%', padding: '0.75rem 1rem 0.75rem 2rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', fontSize: '0.9375rem', boxSizing: 'border-box' }} />
                  </div>
                </div>
              </div>
              {/* Preview */}
              {(newQuotedMaterialQty && newQuotedMaterialPrice) && (
                <div style={{ padding: '0.75rem 1rem', background: 'var(--green-tint-light)', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: 'var(--green-text)', fontSize: '0.875rem', fontWeight: '500' }}>Total:</span>
                  <span style={{ color: 'var(--green-text)', fontSize: '1rem', fontWeight: '700' }}>${formatCurrency((parseInput(newQuotedMaterialQty) || 0) * (parseInput(newQuotedMaterialPrice) || 0))}</span>
                </div>
              )}
            </div>
            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', background: 'var(--bg-secondary)' }}>
              <button onClick={() => setShowAddQuotedMaterialsModal(false)} style={{ padding: '0.75rem 1.5rem', border: '1.5px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)', color: 'var(--text-muted)', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
              <button onClick={addQuotedMaterialsItem} style={{ padding: '0.75rem 1.5rem', border: 'none', borderRadius: '8px', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: '#ffffff', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>Add Material</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Shorthand Estimate */}
      <div className="shorthand-section">
        <div className="shorthand-card">
          <h3 className="shorthand-title">SHORTHAND ESTIMATE</h3>
          <div className="shorthand-inputs-grid">
            <div className="shorthand-input-card">
              <div className="shorthand-input-label">Estimated Material Costs</div>
              <div className="shorthand-input-wrapper">
                <span className="input-prefix">$</span>
                <input
                  type="text"
                  className="shorthand-input"
                  value={shorthandMaterialCostInput}
                  onChange={(e) => setShorthandMaterialCostInput(e.target.value)}
                  onBlur={async () => {
                    const parsed = parseInput(shorthandMaterialCostInput) || 0;
                    const formatted = formatNumber(parsed);
                    setShorthandMaterialCost(parsed);
                    setShorthandMaterialCostInput(formatted);
                    try {
                      await supabase
                        .from('estimations')
                        .update({ shorthand_material_cost: parsed })
                        .eq('id', estimationId);
                      console.log('✅ Saved shorthand material cost');
                    } catch (error) {
                      console.error('Error saving shorthand material cost:', error);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                  }}
                />
              </div>
            </div>
            
            <div className="shorthand-input-card">
              <div className="shorthand-input-label">Estimated Labor Hours</div>
              <div className="shorthand-input-wrapper">
                <input
                  type="text"
                  className="shorthand-input"
                  value={shorthandLaborHoursInput}
                  onChange={(e) => setShorthandLaborHoursInput(e.target.value)}
                  onBlur={async () => {
                    const parsed = parseInput(shorthandLaborHoursInput) || 0;
                    const formatted = formatNumber(parsed);
                    setShorthandLaborHours(parsed);
                    setShorthandLaborHoursInput(formatted);
                    try {
                      await supabase
                        .from('estimations')
                        .update({ shorthand_labor_hours: parsed })
                        .eq('id', estimationId);
                      console.log('✅ Saved shorthand labor hours');
                    } catch (error) {
                      console.error('Error saving shorthand labor hours:', error);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                  }}
                />
              </div>
            </div>
            
            <div className="shorthand-input-card">
              <div className="shorthand-input-label">PM Labor Rate</div>
              <div className="shorthand-input-wrapper">
                <span className="input-prefix">$</span>
                <input
                  type="text"
                  className="shorthand-input"
                  value={shorthandPMLaborRateInput}
                  onChange={(e) => setShorthandPMLaborRateInput(e.target.value)}
                  onBlur={async () => {
                    const parsed = parseInput(shorthandPMLaborRateInput) || 0;
                    const formatted = formatNumber(parsed);
                    setShorthandPMLaborRate(parsed);
                    setShorthandPMLaborRateInput(formatted);
                    try {
                      await supabase
                        .from('estimations')
                        .update({ shorthand_pm_labor_rate: parsed })
                        .eq('id', estimationId);
                      console.log('✅ Saved shorthand PM labor rate');
                    } catch (error) {
                      console.error('Error saving shorthand PM labor rate:', error);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                  }}
                />
              </div>
            </div>
            
            <div className="shorthand-input-card">
              <div className="shorthand-input-label">Estimated PM Hours</div>
              <div className="shorthand-input-wrapper">
                <input
                  type="text"
                  className="shorthand-input"
                  value={shorthandPMHoursInput}
                  onChange={(e) => setShorthandPMHoursInput(e.target.value)}
                  onBlur={async () => {
                    const parsed = parseInput(shorthandPMHoursInput) || 0;
                    const formatted = formatNumber(parsed);
                    setShorthandPMHours(parsed);
                    setShorthandPMHoursInput(formatted);
                    try {
                      await supabase
                        .from('estimations')
                        .update({ shorthand_pm_hours: parsed })
                        .eq('id', estimationId);
                      console.log('✅ Saved shorthand PM hours');
                    } catch (error) {
                      console.error('Error saving shorthand PM hours:', error);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                  }}
                />
              </div>
            </div>
          </div>
          
          <div className="shorthand-breakdown">
            <div className="shorthand-row">
              <span className="shorthand-label">Subtotal</span>
              <span className="shorthand-value">${formatCurrency(shorthand.subtotal)}</span>
            </div>
            <div className="shorthand-row">
              <span className="shorthand-label">
                Material Tax <span className="material-tax-badge">{materialTaxRate}%</span>
              </span>
              <span className="shorthand-value">${formatCurrency(shorthandMaterialCost * (materialTaxRate / 100))}</span>
            </div>
            <div className="shorthand-row">
              <span className="shorthand-label">
                Overhead <span className="overhead-badge">{overhead}%</span>
              </span>
              <span className="shorthand-value">${formatCurrency(shorthand.overheadAmount)}</span>
            </div>
            <div className="shorthand-row">
              <span className="shorthand-label">
                Net Profit <span className="profit-badge">{profit}%</span>
              </span>
              <span className="shorthand-value profit-value">${formatCurrency(shorthand.profitAmount)}</span>
            </div>
            <div className="shorthand-total-row">
              <span>Total</span>
              <span>${formatCurrency(shorthand.total)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Longhand Estimate - Below Shorthand */}
      <div className="financial-breakdown">
        <div className="financial-card">
          <h3 className="financial-title">LONGHAND ESTIMATE</h3>
          
          <div className="financial-rows">
            <div className="financial-row">
              <span className="financial-label">Subtotal</span>
              <span className="financial-value">${formatCurrency(totals.subtotal)}</span>
            </div>
            
            <div className="financial-row">
              <span className="financial-label">
                Material Tax <span className="material-tax-badge">{materialTaxRate}%</span>
              </span>
              <span className="financial-value">${formatCurrency(totals.materialCost * (materialTaxRate / 100))}</span>
            </div>
            
            {totals.pmTotal > 0 && (
              <div className="financial-row">
                <span className="financial-label">Project Management</span>
                <span className="financial-value">${formatCurrency(totals.pmTotal)}</span>
              </div>
            )}
            
            {totals.miscTotal > 0 && (
              <div className="financial-row">
                <span className="financial-label">Miscellaneous</span>
                <span className="financial-value">${formatCurrency(totals.miscTotal)}</span>
              </div>
            )}
            
            <div className="financial-row">
              <span className="financial-label">
                Overhead <span className="overhead-badge">{overhead}%</span>
              </span>
              <span className="financial-value">${formatCurrency(totals.overheadAmount)}</span>
            </div>
            
            <div className="financial-row profit-highlight">
              <span className="financial-label">
                Net Profit <span className="profit-badge">{profit}%</span>
              </span>
              <span className="financial-value profit-value">${formatCurrency(totals.profitAmount)}</span>
            </div>
            
            <div className="financial-divider"></div>
            
            <div className="financial-total-row">
              <div className="total-label">ESTIMATED TOTAL</div>
              <div className="total-value">${formatCurrency(totals.total + (materialTaxRate > 0 && totals.materialCost > 0 ? totals.materialCost * (materialTaxRate / 100) : 0) + totals.pmTotal + totals.miscTotal)}</div>
            </div>
            
            {totals.total > 0 && (
              <>
                <div className="breakdown-bar">
                  <div className="bar-segment cost-segment" style={{ width: `${(totals.subtotal / (totals.total + totals.materialCost * (materialTaxRate / 100))) * 100}%` }}></div>
                  <div className="bar-segment tax-segment" style={{ width: `${((totals.materialCost * (materialTaxRate / 100)) / (totals.total + totals.materialCost * (materialTaxRate / 100))) * 100}%` }}></div>
                  <div className="bar-segment overhead-segment" style={{ width: `${(totals.overheadAmount / (totals.total + totals.materialCost * (materialTaxRate / 100))) * 100}%` }}></div>
                  <div className="bar-segment profit-segment" style={{ width: `${(totals.profitAmount / (totals.total + totals.materialCost * (materialTaxRate / 100))) * 100}%` }}></div>
                </div>
                <div className="breakdown-bar-labels">
                  <span>COST BASIS</span>
                  <span>PROFIT MARGIN</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Add Item Modal */}
      {showAddItemModal && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
            padding: '2rem',
            overflow: 'hidden'
          }}
          onClick={resetModal}
        >
          <div 
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '16px',
              maxWidth: '550px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '1.5rem 1.5rem 1rem',
              borderBottom: '1px solid var(--border-color)'
            }}>
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: 'var(--text-primary)',
                margin: '0 0 1rem 0'
              }}>
                Add New Item
              </h2>
              
              {/* Mode Selector */}
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                background: 'var(--bg-secondary)',
                padding: '0.375rem',
                borderRadius: '8px'
              }}>
                <button
                  onClick={() => setAddItemMode('electrical')}
                  style={{
                    flex: 1,
                    padding: '0.625rem 1rem',
                    border: 'none',
                    borderRadius: '6px',
                    background: addItemMode === 'electrical' ? '#3b82f6' : 'transparent',
                    color: addItemMode === 'electrical' ? '#ffffff' : 'var(--text-muted)',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Electrical Component
                </button>
                <button
                  onClick={() => setAddItemMode('pm')}
                  style={{
                    flex: 1,
                    padding: '0.625rem 1rem',
                    border: 'none',
                    borderRadius: '6px',
                    background: addItemMode === 'pm' ? '#3b82f6' : 'transparent',
                    color: addItemMode === 'pm' ? '#ffffff' : 'var(--text-muted)',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Project Management
                </button>
                <button
                  onClick={() => setAddItemMode('misc')}
                  style={{
                    flex: 1,
                    padding: '0.625rem 1rem',
                    border: 'none',
                    borderRadius: '6px',
                    background: addItemMode === 'misc' ? '#3b82f6' : 'transparent',
                    color: addItemMode === 'misc' ? '#ffffff' : 'var(--text-muted)',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Miscellaneous
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div style={{
              padding: '1.5rem',
              overflowY: 'auto',
              flex: 1,
              minHeight: 0
            }}>
              {addItemMode === 'electrical' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: 'var(--text-secondary)'
                    }}>Component Name</label>
                    <input
                      type="text"
                      value={newItemName}
                      onChange={(e) => setNewItemName(e.target.value)}
                      placeholder="e.g., Duplex Receptacle"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: '1.5px solid var(--border-color)',
                        borderRadius: '8px',
                        fontSize: '0.9375rem',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: 'var(--text-secondary)'
                    }}>Description / Measurement</label>
                    <input
                      type="text"
                      value={newItemDescription}
                      onChange={(e) => setNewItemDescription(e.target.value)}
                      placeholder="e.g., 20A 125V or Custom"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: '1.5px solid var(--border-color)',
                        borderRadius: '8px',
                        fontSize: '0.9375rem',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: 'var(--text-secondary)'
                      }}>Quantity</label>
                      <input
                        type="text"
                        value={newItemQuantity}
                        onChange={(e) => setNewItemQuantity(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          border: '1.5px solid var(--border-color)',
                          borderRadius: '8px',
                          fontSize: '0.9375rem',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: 'var(--text-secondary)'
                      }}>Unit</label>
                      <select
                        value={newItemUnit}
                        onChange={(e) => setNewItemUnit(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          border: '1.5px solid var(--border-color)',
                          borderRadius: '8px',
                          fontSize: '0.9375rem',
                          boxSizing: 'border-box',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="EA">EA</option>
                        <option value="FT">FT</option>
                        <option value="LF">LF</option>
                        <option value="SQ FT">SQ FT</option>
                        <option value="HR">HR</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: 'var(--text-secondary)'
                    }}>NECA Condition (hours per unit)</label>
                    <input
                      type="text"
                      value={newItemLaborHours}
                      onChange={(e) => setNewItemLaborHours(e.target.value)}
                      placeholder="0.00"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: '1.5px solid var(--border-color)',
                        borderRadius: '8px',
                        fontSize: '0.9375rem',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: 'var(--text-secondary)'
                    }}>Material Price (per unit)</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{
                        position: 'absolute',
                        left: '1rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--text-muted)',
                        fontSize: '0.9375rem',
                        fontWeight: '500'
                      }}>$</span>
                      <input
                        type="text"
                        value={newItemMaterialPrice}
                        onChange={(e) => setNewItemMaterialPrice(e.target.value)}
                        placeholder="0.00"
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem 0.75rem 2rem',
                          border: '1.5px solid var(--border-color)',
                          borderRadius: '8px',
                          fontSize: '0.9375rem',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {addItemMode === 'pm' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: 'var(--text-secondary)'
                    }}>PM Labor Hours</label>
                    <input
                      type="text"
                      value={newPMHours}
                      onChange={(e) => setNewPMHours(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: '1.5px solid var(--border-color)',
                        borderRadius: '8px',
                        fontSize: '0.9375rem',
                        boxSizing: 'border-box'
                      }}
                    />
                    <p style={{
                      margin: '0.5rem 0 0',
                      fontSize: '0.75rem',
                      color: 'var(--text-muted)'
                    }}>
                      Default is 1/5 of total labor hours ({formatNumber(totals.laborHours / 5)} hrs)
                    </p>
                  </div>
                  
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: 'var(--text-secondary)'
                    }}>PM Hourly Rate</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{
                        position: 'absolute',
                        left: '1rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--text-muted)',
                        fontSize: '0.9375rem',
                        fontWeight: '500'
                      }}>$</span>
                      <input
                        type="text"
                        value={newPMRate}
                        onChange={(e) => setNewPMRate(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem 0.75rem 2rem',
                          border: '1.5px solid var(--border-color)',
                          borderRadius: '8px',
                          fontSize: '0.9375rem',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {addItemMode === 'misc' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: 'var(--text-secondary)'
                    }}>Item Name</label>
                    <input
                      type="text"
                      value={newItemName}
                      onChange={(e) => setNewItemName(e.target.value)}
                      placeholder="e.g., Equipment Rental, Permits"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: '1.5px solid var(--border-color)',
                        borderRadius: '8px',
                        fontSize: '0.9375rem',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: 'var(--text-secondary)'
                    }}>Description (Optional)</label>
                    <input
                      type="text"
                      value={newItemDescription}
                      onChange={(e) => setNewItemDescription(e.target.value)}
                      placeholder="Additional details..."
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: '1.5px solid var(--border-color)',
                        borderRadius: '8px',
                        fontSize: '0.9375rem',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: 'var(--text-secondary)'
                    }}>Total Price</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{
                        position: 'absolute',
                        left: '1rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--text-muted)',
                        fontSize: '0.9375rem',
                        fontWeight: '500'
                      }}>$</span>
                      <input
                        type="text"
                        value={newMiscPrice}
                        onChange={(e) => setNewMiscPrice(e.target.value)}
                        placeholder="0.00"
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem 0.75rem 2rem',
                          border: '1.5px solid var(--border-color)',
                          borderRadius: '8px',
                          fontSize: '0.9375rem',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div style={{
              padding: '1rem 1.5rem',
              borderTop: '1px solid var(--border-color)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.75rem',
              background: 'var(--bg-secondary)',
              flexShrink: 0
            }}>
              <button
                onClick={resetModal}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '1.5px solid var(--border-color)',
                  borderRadius: '8px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-muted)',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (addItemMode === 'electrical') addElectricalComponent();
                  else if (addItemMode === 'pm') addPMItem();
                  else addMiscItem();
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: '#ffffff',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                }}
              >
                Add Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Order Export Modal - Portal to body for full-screen blur */}
      {showChangeOrderModal && createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '2rem'
          }}
          onClick={() => setShowChangeOrderModal(false)}
        >
          <div 
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '1.25rem 1.5rem',
              borderBottom: '1px solid var(--border-color)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.125rem',
                fontWeight: '600',
                color: 'var(--text-primary)'
              }}>
                Export Change Order
              </h3>
              <button
                onClick={() => setShowChangeOrderModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: 'var(--text-placeholder)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <span style={{ fontSize: '20px', lineHeight: 1 }}>×</span>
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '1.5rem' }}>
              {/* CCN Field */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: 'var(--text-secondary)'
                }}>
                  CCN # (Change Control Number)
                </label>
                <input
                  type="text"
                  value={changeOrderCCN}
                  onChange={(e) => setChangeOrderCCN(e.target.value)}
                  placeholder="e.g., 18006"
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.875rem',
                    border: '1px solid var(--border-strong)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    color: 'var(--text-primary)',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Client Address Field */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: 'var(--text-secondary)'
                }}>
                  Client Address
                </label>
                <input
                  type="text"
                  value={changeOrderClientAddress}
                  onChange={(e) => setChangeOrderClientAddress(e.target.value)}
                  placeholder="e.g., 123 Main St, City, State 12345"
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.875rem',
                    border: '1px solid var(--border-strong)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    color: 'var(--text-primary)',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Work Description Field */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: 'var(--text-secondary)'
                }}>
                  Work Description
                </label>
                <textarea
                  value={changeOrderWorkDescription}
                  onChange={(e) => setChangeOrderWorkDescription(e.target.value)}
                  placeholder="Describe the work to be performed..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.875rem',
                    border: '1px solid var(--border-strong)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    color: 'var(--text-primary)',
                    outline: 'none',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1rem 1.5rem',
              borderTop: '1px solid var(--border-color)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.75rem'
            }}>
              <button
                onClick={() => {
                  setShowChangeOrderModal(false);
                  setChangeOrderCCN('');
                  setChangeOrderClientAddress('');
                  setChangeOrderWorkDescription('');
                }}
                style={{
                  padding: '0.625rem 1rem',
                  border: '1px solid var(--border-strong)',
                  borderRadius: '6px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-secondary)',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  exportChangeOrder();
                  setShowChangeOrderModal(false);
                  setChangeOrderCCN('');
                  setChangeOrderClientAddress('');
                  setChangeOrderWorkDescription('');
                }}
                style={{
                  padding: '0.625rem 1rem',
                  border: 'none',
                  borderRadius: '6px',
                  background: 'var(--text-primary)',
                  color: 'var(--bg-primary)',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Export Change Order
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

